import { CommonModule } from '@angular/common';
import { Component, inject, ChangeDetectorRef } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { CategoryService } from 'src/app/services/category';
import { LocaleService } from 'src/app/services/locale';
import { DropdownComponent } from '../../../../shared/dropdown';
import { TrashComponent } from '../../../../icons/trash';
import { PlusComponent } from '../../../../icons/plus';
import { CountryPickerComponent } from '../../../../shared/country-picker';
import { YearPickerComponent } from '../../../../shared/year-picker';
import { DatePickerComponent } from 'src/app/components/shared/date-picker';
import { fileUploadlogoComponent } from 'src/app/components/icons/file-upload';

// Define interfaces for our form data
interface AverageDataBuildingRow {
  assetLabel: FormControl<string | null>;
  buildingType: FormControl<string | null>;
  epcRating: FormControl<string | null>;
  country: FormControl<string | null>;
  reportingYear: FormControl<string | null>;
  floorArea: FormControl<string | null>;
  method: FormControl<'average'>;
  assetType: FormControl<'building'>;
}

interface AverageDataVehicleRow {
  assetLabel: FormControl<string | null>;
  assetCategory: FormControl<string | null>;
  subtype: FormControl<string | null>;
  country: FormControl<string | null>;
  reportingYear: FormControl<string | null>;
  quantity: FormControl<string | null>;
  annualDistance: FormControl<string | null>;
  method: FormControl<'average'>;
  assetType: FormControl<'vehicle'>;
}

interface SubMeteredEntry {
  category: FormControl<string | null>;
  type: FormControl<string | null>;
  unit: FormControl<string | null>;
  amount: FormControl<string | null>;
  isSubMetered: FormControl<true>;
}

interface NonSubMeteredEntry {
  category: FormControl<string | null>;
  type: FormControl<string | null>;
  unit: FormControl<string | null>;
  totalBuildingEnergy: FormControl<string | null>;
  totalBuildingArea: FormControl<string | null>;
  leasedArea: FormControl<string | null>;
  occupancyRate: FormControl<number>;
  isSubMetered: FormControl<false>;
}

type EnergyEntry = FormGroup<SubMeteredEntry> | FormGroup<NonSubMeteredEntry>;

interface AssetSpecificBuildingRow {
  assetLabel: FormControl<string | null>;
  reportingYear: FormControl<string | null>;
  country: FormControl<string | null>;
  subMetered: FormControl<boolean>;
  method: FormControl<'asset-specific'>;
  assetType: FormControl<'building'>;
}

interface AssetSpecificVehicleRow {
  assetLabel: FormControl<string | null>;
  reportingYear: FormControl<string | null>;
  country: FormControl<string | null>;
  assetCategory: FormControl<string | null>;
  subtype: FormControl<string | null>;
  quantity: FormControl<string | null>;
  annualDistance: FormControl<string | null>;
  method: FormControl<'asset-specific'>;
  assetType: FormControl<'vehicle'>;
}

interface LessorSpecificRow {
  lessorName: FormControl<string | null>;
  assetType: FormControl<string | null>;
  reportingYear: FormControl<string | null>;
  country: FormControl<string | null>;
  scope1Emissions: FormControl<string | null>;
  scope2Emissions: FormControl<string | null>;
  leasedQuantity: FormControl<string | null>;
  totalQuantity: FormControl<string | null>;
  occupancyRate: FormControl<number>;
  method: FormControl<'lessor-specific'>;
}

// New interfaces for leased-out assets
interface AverageDataLeasedBuildingRow {
  assetLabel: FormControl<string | null>;
  buildingType: FormControl<string | null>;
  epcRating: FormControl<string | null>;
  country: FormControl<string | null>;
  reportingYear: FormControl<string | null>;
  floorArea: FormControl<string | null>;
  lesseeEntity: FormControl<string | null>;
  leaseStartDate: FormControl<string | null>;
  leaseEndDate: FormControl<string | null>;
  method: FormControl<'average'>;
  assetType: FormControl<'building'>;
}

interface AverageDataLeasedOtherAssetRow {
  assetLabel: FormControl<string | null>;
  assetCategory: FormControl<string | null>;
  subtype: FormControl<string | null>;
  country: FormControl<string | null>;
  reportingYear: FormControl<string | null>;
  quantity: FormControl<string | null>;
  utilizationMetric: FormControl<string | null>;
  lesseeEntity: FormControl<string | null>;
  leaseStartDate: FormControl<string | null>;
  leaseEndDate: FormControl<string | null>;
  method: FormControl<'average'>;
  assetType: FormControl<'other'>;
}

interface AssetSpecificLeasedBuildingEnergyEntry {
  category: FormControl<string | null>;
  type: FormControl<string | null>;
  unit: FormControl<string | null>;
  amount: FormControl<string | null>;
  evidence: FormControl<string | null>;
  isSubMetered: FormControl<true>;
}

interface AssetSpecificLeasedBuildingAllocationEntry {
  category: FormControl<string | null>;
  type: FormControl<string | null>;
  unit: FormControl<string | null>;
  totalBuildingEnergy: FormControl<string | null>;
  totalBuildingArea: FormControl<string | null>;
  leasedArea: FormControl<string | null>;
  occupancyRate: FormControl<number | null>;
  evidence: FormControl<string | null>;
  isSubMetered: FormControl<false>;
}

type AssetSpecificLeasedBuildingEntry =
  | FormGroup<AssetSpecificLeasedBuildingEnergyEntry>
  | FormGroup<AssetSpecificLeasedBuildingAllocationEntry>;

interface AssetSpecificLeasedBuildingRow {
  assetLabel: FormControl<string | null>;
  lesseeEntity: FormControl<string | null>;
  reportingYear: FormControl<string | null>;
  country: FormControl<string | null>;
  leaseStartDate: FormControl<string | null>;
  leaseEndDate: FormControl<string | null>;
  subMetered: FormControl<boolean>;
  occupancyRate: FormControl<number>;
  method: FormControl<'asset-specific'>;
  assetType: FormControl<'building'>;
}

interface AssetSpecificLeasedOtherAssetRow {
  assetLabel: FormControl<string | null>;
  lesseeEntity: FormControl<string | null>;
  assetCategory: FormControl<string | null>;
  subtype: FormControl<string | null>;
  reportingYear: FormControl<string | null>;
  country: FormControl<string | null>;
  leaseStartDate: FormControl<string | null>;
  leaseEndDate: FormControl<string | null>;
  utilizationMetric: FormControl<string | null>;
  method: FormControl<'asset-specific'>;
  assetType: FormControl<'other'>;
  annualDistance: FormControl<string | null>;
}

interface AssetSpecificLeasedOtherAssetEnergyEntry {
  fuelType: FormControl<string | null>;
  unit: FormControl<string | null>;
  amount: FormControl<string | null>;
  evidence: FormControl<string | null>;
}

interface LesseeSpecificRow {
  assetLabel: FormControl<string | null>;
  assetType: FormControl<string | null>;
  lesseeEntity: FormControl<string | null>;
  reportingYear: FormControl<string | null>;
  country: FormControl<string | null>;
  leaseStartDate: FormControl<string | null>;
  leaseEndDate: FormControl<string | null>;
  scope1Emissions: FormControl<string | null>;
  scope2Emissions: FormControl<string | null>;
  scope2Basis: FormControl<string | null>;
  allocationMetric: FormControl<string | null>;
  leasedQuantity: FormControl<string | null>;
  totalQuantity: FormControl<string | null>;
  occupancyRate: FormControl<number | null>;
  method: FormControl<'lessee-specific'>;
  evidence: FormControl<any | null>;
  lesseeEvidence: FormControl<any | null>;
}

type FormRow =
  | { row: AverageDataBuildingRow }
  | { row: AverageDataVehicleRow }
  | { row: AssetSpecificBuildingRow; energyEntries?: EnergyEntry[] }
  | { row: AssetSpecificVehicleRow }
  | { row: LessorSpecificRow }
  | { row: AverageDataLeasedBuildingRow }
  | { row: AverageDataLeasedOtherAssetRow }
  | {
    row: AssetSpecificLeasedBuildingRow;
    entries?: AssetSpecificLeasedBuildingEntry[];
  }
  | {
    row: AssetSpecificLeasedOtherAssetRow;
    energyEntries?: FormGroup<AssetSpecificLeasedOtherAssetEnergyEntry>[];
  }
  | { row: LesseeSpecificRow };

@Component({
  selector: 'app-category5-question2',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    DropdownComponent,
    TrashComponent,
    PlusComponent,
    CountryPickerComponent,
    YearPickerComponent,
    DatePickerComponent,
    fileUploadlogoComponent
  ],
  template: `
    <div class="text-[12px] text-[#616161] mt-1">
      {{ strings[language].answer }}
    </div>


    <div class="container mx-auto">
      <div class="flex flex-wrap gap-4 sm:gap-6 mt-4 mb-4 ml-2">
        <div
          (click)="toggleUsage(true)"
          class="cursor-pointer flex gap-2 items-center"
        >
          <div
            [class.bg-green-950]="usage"
            class="w-4 h-4 border rounded-full border-gray-400 flex-shrink-0"
          ></div>
          <span>{{ strings[language].choice_yes }}</span>
        </div>
        <div
          (click)="toggleUsage(false)"
          class="cursor-pointer flex gap-2 items-center"
        >
          <div
            [class.bg-green-950]="!usage"
            class="w-4 h-4 border rounded-full border-gray-400 flex-shrink-0"
          ></div>
          <span>{{ strings[language].choice_no }}</span>
        </div>
      </div>
      <div *ngIf="usage" class="space-y-8">
        <!-- Method Selection -->
        <div class="border rounded-lg p-4">
          <div class="flex flex-wrap gap-4">
            <!-- Average Method -->
            <div
              (click)="selectMethod('average')"
              class="flex gap-2 items-center cursor-pointer flex-1 min-w-[200px]"
            >
              <div
                [ngClass]="{ 'bg-[#183A25]': selectedMethod === 'average' }"
                class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
              ></div>
              <div class="font-medium">
                {{ strings[language].averageMethod }}
              </div>
              <div class="relative group ml-1 mt-1">
                <button
                  type="button"
                  class="text-gray-500 hover:text-gray-700 focus:outline-none"
                  (click)="$event.stopPropagation()"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
                <div
                  class="absolute z-10 hidden group-hover:block w-[400px] bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
                >
                  <p class="mb-2">{{ strings[language].averageMethodDescLine1 }}</p>
<p class="mb-2">{{ strings[language].averageMethodDescLine2 }}</p>
<ul>
  <li class="mb-2">{{ strings[language].averageMethodDescLine3 }}</li>
  <li class="mb-2">{{ strings[language].averageMethodDescLine4 }}</li>
</ul>
                </div>
              </div>
            </div>

            <!-- Asset-Specific Method -->
            <div
              (click)="selectMethod('asset-specific')"
              class="flex gap-2 items-center cursor-pointer flex-1 min-w-[200px]"
            >
              <div
                [ngClass]="{
                  'bg-[#183A25]': selectedMethod === 'asset-specific'
                }"
                class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
              ></div>
              <div class="font-medium">
                {{ strings[language].assetSpecificMethod }}
              </div>
              <div class="relative group ml-1 mt-1">
                <button
                  type="button"
                  class="text-gray-500 hover:text-gray-700 focus:outline-none"
                  (click)="$event.stopPropagation()"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
                <div
                  class="absolute z-10 hidden group-hover:block w-[400px] bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
                >
                  {{ strings[language].assetSpecificMethodDesc }}
                </div>
              </div>
            </div>

            <!-- Lessee-Specific Method -->
            <div
              (click)="selectMethod('lessee-specific')"
              class="flex gap-2 items-center cursor-pointer flex-1 min-w-[200px]"
            >
              <div
                [ngClass]="{
                  'bg-[#183A25]': selectedMethod === 'lessee-specific'
                }"
                class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
              ></div>
              <div class="font-medium">
                {{ strings[language].lesseeSpecificMethod }}
              </div>
              <div class="relative group ml-1 mt-1">
                <button
                  type="button"
                  class="text-gray-500 hover:text-gray-700 focus:outline-none"
                  (click)="$event.stopPropagation()"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
                <div
                  class="absolute z-10 hidden group-hover:block w-[400px] bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
                >
                  {{ strings[language].lesseeSpecificMethodDesc }}



                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Average Data Method -->
        <div *ngIf="selectedMethod === 'average'" class="space-y-6">
          <!-- Leased Buildings Section -->
          <div>
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium">
                {{ strings[language].leasedBuildings }}
              </h3>
              <button
                class="bg-[#F8F9FA] px-3 py-2 rounded text-white"
                (click)="addAverageLeasedBuildingRow()"
              >
                <app-plus></app-plus>
              </button>
            </div>

            <div class="space-y-4">
              <div
                *ngFor="
                  let control of averageLeasedBuildingControls;
                  let i = index
                "
                class="border rounded-lg p-4"
              >
                <div class="flex justify-end mb-4 cursor-pointer">
                  <button
                    (click)="removeRow('average-leased-building', i)"
                    class="text-red-500"
                  >
                    <app-trash></app-trash>
                  </button>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].assetLabel
                    }}</label>
                    <app-input
                      placeholder="{{
                        strings[language].assetLabelPlaceholder
                      }}"
                      [control]="control.row.assetLabel"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].buildingType
                    }}</label>
                    <app-dropdown
                      placeholder="{{ strings[language].buildingType }}"
                      [options]="buildingTypes"
                      [control]="control.row.buildingType"
                      (change)="onChange()"
                    ></app-dropdown>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].epcRating
                    }}</label>
                    <app-dropdown
                      placeholder="EPC Rating"
                      [options]="epcRatings"
                      [control]="control.row.epcRating"
                      (change)="onChange()"
                    ></app-dropdown>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].country
                    }}</label>
                    <app-country-picker
                      [control]="control.row.country"
                      (change)="onChange()"
                    ></app-country-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].reportingYear
                    }}</label>
                    <app-year-picker
                      [control]="control.row.reportingYear"
                      (yearChange)="onChange()"
                    ></app-year-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].floorArea
                    }}</label>
                    <app-input
                      type="number"
                      placeholder="0"
                      unit="m²"
                      [control]="control.row.floorArea"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].lesseeEntity
                    }}</label>
                    <app-input
                      placeholder="{{
                        strings[language].lesseeEntityPlaceholder
                      }}"
                      [control]="control.row.lesseeEntity"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].leaseStartDate }}
                    </label>
                    <app-date-picker
                      [control]="control.row.leaseStartDate"
                      [maxDate]="control.row.leaseEndDate.value"
                      (dateChange)="
                        onDateChange($event, control.row.leaseStartDate)
                      "
                    ></app-date-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].leaseEndDate }}
                    </label>
                    <app-date-picker
                      [control]="control.row.leaseEndDate"
                      [minDate]="control.row.leaseStartDate.value"
                      [isEndDatePicker]="true"
                      (dateChange)="
                        onDateChange($event, control.row.leaseEndDate)
                      "
                    ></app-date-picker>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Other Leased Assets Section -->
          <div>
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium">
                {{ strings[language].otherLeasedAssets }}
              </h3>
              <button
                class="bg-[#F8F9FA] px-3 py-2 rounded text-white"
                (click)="addAverageLeasedOtherAssetRow()"
              >
                <app-plus></app-plus>
              </button>
            </div>

            <div class="space-y-4">
              <div
                *ngFor="
                  let control of averageLeasedOtherAssetControls;
                  let i = index
                "
                class="border rounded-lg p-4"
              >
                <div class="flex justify-end mb-4 cursor-pointer">
                  <button
                    (click)="removeRow('average-leased-other', i)"
                    class="text-red-500"
                  >
                    <app-trash></app-trash>
                  </button>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].assetLabel
                    }}</label>
                    <app-input
                      placeholder="{{
                        strings[language].assetLabelPlaceholder
                      }}"
                      [control]="control.row.assetLabel"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].assetCategory
                    }}</label>
                    <app-dropdown
                      placeholder="Asset Category"
                      [options]="vehicleCategories"
                      [control]="control.row.assetCategory"
                      (change)="onChange()"
                    ></app-dropdown>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].subtype
                    }}</label>
                    <app-dropdown
                      placeholder="Subtype/Description"
                      [options]="
                        getVehicleSubtypes(control.row.assetCategory.value)
                      "
                      [control]="control.row.subtype"
                      (change)="onChange()"
                    ></app-dropdown>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].country
                    }}</label>
                    <app-country-picker
                      [control]="control.row.country"
                      (change)="onChange()"
                    ></app-country-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].reportingYear
                    }}</label>
                    <app-year-picker
                      [control]="control.row.reportingYear"
                      (yearChange)="onChange()"
                    ></app-year-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].quantity
                    }}</label>
                    <app-input
                      type="number"
                      placeholder="0"
                      [control]="control.row.quantity"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].annualDistance
                    }}</label>
                    <!-- <app-dropdown
                      placeholder="{{
                        strings[language].selectUtilizationMetric
                      }}"
                      [options]="utilizationMetrics"
                      [control]="control.row.utilizationMetric"
                      (change)="onChange()"
                    ></app-dropdown> -->
                    <app-input
                      placeholder="{{
                        strings[language].lesseeEntityPlaceholder
                      }}"
                      [control]="control.row.utilizationMetric"
                      (change)="onChange()"
                    ></app-input>
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].lesseeEntity }}
                    </label>
                    <app-input
                      placeholder="{{
                        strings[language].lesseeEntityPlaceholder
                      }}"
                      [control]="control.row.lesseeEntity"
                      (change)="onChange()"
                    ></app-input>
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].leaseStartDate }}
                    </label>
                    <app-date-picker
                      [control]="control.row.leaseStartDate"
                      [maxDate]="control.row.leaseEndDate.value"
                      (dateChange)="
                        onDateChange($event, control.row.leaseStartDate)
                      "
                    ></app-date-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].leaseEndDate }}
                    </label>
                    <app-date-picker
                      [control]="control.row.leaseEndDate"
                      [minDate]="control.row.leaseStartDate.value"
                      [isEndDatePicker]="true"
                      (dateChange)="
                        onDateChange($event, control.row.leaseEndDate)
                      "
                    ></app-date-picker>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Asset-Specific Method -->
        <div *ngIf="selectedMethod === 'asset-specific'" class="space-y-6">
          <!-- Buildings Section -->
          <div>
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium">
                {{ strings[language].leasedBuildings }}
              </h3>
              <button
                class="bg-[#F8F9FA] px-3 py-2 rounded text-white"
                (click)="addAssetSpecificLeasedBuildingRow()"
              >
                <app-plus></app-plus>
              </button>
            </div>

            <div class="space-y-4">
              <div
                *ngFor="
                  let control of assetSpecificLeasedBuildingControls;
                  let i = index
                "
                class="border rounded-lg p-4"
              >
                <div class="flex justify-between items-center mb-4">
                  <span>{{ strings[language].asset_Level_Block }}</span>
                  <button
                    (click)="removeRow('asset-specific-leased-building', i)"
                    class="text-red-500"
                  >
                    <app-trash></app-trash>
                  </button>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].assetLabel
                    }}</label>
                    <app-input
                      placeholder="{{
                        strings[language].assetLabelPlaceholder
                      }}"
                      [control]="control.row.assetLabel"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].lesseeEntity
                    }}</label>
                    <app-input
                      placeholder="{{
                        strings[language].lesseeEntityPlaceholder
                      }}"
                      [control]="control.row.lesseeEntity"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].reportingYear
                    }}</label>
                    <app-year-picker
                      [control]="control.row.reportingYear"
                      (yearChange)="onChange()"
                    ></app-year-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].country
                    }}</label>
                    <app-country-picker
                      [control]="control.row.country"
                      (change)="onChange()"
                    ></app-country-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].leaseStartDate }}
                    </label>
                    <app-date-picker
                      [control]="control.row.leaseStartDate"
                      [maxDate]="control.row.leaseEndDate.value"
                      (dateChange)="
                        onDateChange($event, control.row.leaseStartDate)
                      "
                    ></app-date-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].leaseEndDate }}
                    </label>
                    <app-date-picker
                      [control]="control.row.leaseEndDate"
                      [minDate]="control.row.leaseStartDate.value"
                      [isEndDatePicker]="true"
                      (dateChange)="
                        onDateChange($event, control.row.leaseEndDate)
                      "
                    ></app-date-picker>
                  </div>

                  <div>
                    <div class="flex gap-1 items-center">
                      <label class="block text-sm font-medium mb-1">
                      {{ strings[language].subMetered }}
                    </label>
                    <div class="relative group ml-1 mt-1">
                <button
                  type="button"
                  class="text-gray-500 hover:text-gray-700 focus:outline-none"
                  (click)="$event.stopPropagation()"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
                <div
                  class="absolute z-10 hidden group-hover:block w-[400px] bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
                >
                  {{ strings[language].subMeteredHelp }}
                </div>
                    </div>
              </div>
                    <div class="flex items-center">
                      <label class="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          class="sr-only peer"
                          [formControl]="control.row.subMetered"
                          (change)="onToggleChange(control)"
                        />
                        <div
                          class="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-gray-300 dark:peer-focus:ring-gray-800 dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-[#043B22]/5 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#043B22]"
                        ></div>
                        <span class="ms-3 text-sm font-medium">
                          {{
                            control.row.subMetered.value
                              ? strings[language].subMeteredLabel
                              : strings[language].notSubMetered
                          }}
                        </span>
                      </label>
                    </div>

                  </div>

                  <div *ngIf="!control.row.subMetered.value">
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].occupancyRate
                    }}</label>
                    <app-input
                      type="number"
                      placeholder="1"
                      min="0"
                      max="1"
                      step="0.1"
                      [control]="control.row.occupancyRate"
                      (change)="onChange()"
                    ></app-input>
                  </div>
                </div>

                <!-- Energy Entries -->
                <div class="mt-4">
                  <div class="flex justify-between items-center mb-4">
                    <h3 class="text-lg font-medium">
                      {{ strings[language].energyEntries }}
                    </h3>

                    <button
                      class="bg-[#F8F9FA] px-3 py-2 rounded text-white"
                      (click)="
                        addAssetSpecificLeasedBuildingEntry(
                          control,
                          control.row.subMetered.value
                        )
                      "
                    >
                      <app-plus></app-plus>
                    </button>
                  </div>

                  <div class="space-y-4">
                    <div
                      *ngFor="
                        let entry of getLeasedBuildingEntries(control);
                        let j = index
                      "
                      class="border rounded p-4"
                    >
                      <div class="flex justify-end mb-2">
                        <button
                          (click)="removeLeasedBuildingEntry(control, j)"
                          class="text-red-500 text-sm"
                        >
                          <app-trash></app-trash>
                        </button>
                      </div>

                      <div
                        *ngIf="
                          getValue(entry, 'isSubMetered');
                          else nonSubMeteredLeasedForm
                        "
                      >
                        <!-- Sub-metered form -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].category
                            }}</label>
                            <app-dropdown
                              placeholder="Category"
                              [options]="energyCategoryOptions"
                              [control]="getFormControl(entry, 'category')"
                              (change)="onCategoryChange(entry); onChange()"
                            ></app-dropdown>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].type
                            }}</label>
                            <app-dropdown
                              [placeholder]="getTypePlaceholder(entry)"
                              [options]="
                                getEnergyTypes(
                                  getFormControl(entry, 'category').value
                                )
                              "
                              [control]="getFormControl(entry, 'type')"
                              (change)="onTypeChange(entry); onChange()"
                            ></app-dropdown>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].unit
                            }}</label>
                            <app-dropdown
                              [placeholder]="getUnitPlaceholder(entry)"
                              [options]="
                                getEnergyUnits(
                                  getFormControl(entry, 'type').value
                                )
                              "
                              [control]="getFormControl(entry, 'unit')"
                            ></app-dropdown>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].amount
                            }}</label>
                            <app-input
                              type="number"
                              placeholder="0"
                              [control]="getFormControl(entry, 'amount')"
                              (change)="onChange()"
                            ></app-input>
                          </div>

                     <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">
                      {{ strings[language].evidence }}
                    </label>
                          <div *ngIf="!getFormControl(entry, 'evidence').value" class="border border-[#CED4DA] flex hover:border-gray-400 items-center justify-between ng-star-inserted p-2 rounded-lg">
  <span class="text-sm text-gray-500 truncate">
    {{ strings[language].selectFile }}
  </span>
  <label>
    <app-file-upload-logo/>
    <input
      type="file"
      class="hidden"
      accept=".pdf"
      (change)="onFileChange($event, getFormControl(entry, 'evidence'))"
    />
  </label>
</div>

<div *ngIf="getFormControl(entry, 'evidence').value" class="bg-white border border-[#CED4DA] flex items-center justify-between ng-star-inserted p-3 rounded-lg">
  <span class="text-sm text-gray-700 truncate">
    {{ getFormControl(entry, 'evidence').value.name }}
  </span>
  <div class="flex gap-2">
    <button
      type="button"
      class="text-[#043B22] hover:text-[#032A1A]"
      (click)="getFormControl(entry, 'evidence').setValue(null); onChange()"
    >
      <app-trash></app-trash>
    </button>
    <label class="cursor-pointer text-[#043B22] hover:text-[#032A1A]">
      <input
        type="file"
        class="hidden"
        accept=".pdf"
        (change)="onFileChange($event, getFormControl(entry, 'evidence'))"
      />
    </label>
  </div>
</div>
                     </div>
                        </div>
                      </div>

                      <ng-template #nonSubMeteredLeasedForm>
                        <!-- Non-sub-metered form -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].category
                            }}</label>
                            <app-dropdown
                              placeholder="Category"
                              [options]="energyCategoryOptions"
                              [control]="getFormControl(entry, 'category')"
                              (change)="onCategoryChange(entry); onChange()"
                            ></app-dropdown>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].type
                            }}</label>
                            <app-dropdown
                              placeholder="{{
                                !getFormControl(entry, 'category').value
                                  ? 'Select a category first'
                                  : 'Select type'
                              }}"
                              [options]="
                                getEnergyTypes(
                                  getFormControl(entry, 'category').value
                                )
                              "
                              [control]="getFormControl(entry, 'type')"
                              (change)="onTypeChange(entry); onChange()"
                            ></app-dropdown>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].unit
                            }}</label>
                            <app-dropdown
                              placeholder="{{
                                !getFormControl(entry, 'type').value
                                  ? 'Select a type first'
                                  : 'Unit will auto-select'
                              }}"
                              [options]="
                                getEnergyUnits(
                                  getFormControl(entry, 'type').value
                                )
                              "
                              [control]="getFormControl(entry, 'unit')"
                            ></app-dropdown>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].totalBuildingEnergy
                            }}</label>
                            <app-input
                              type="number"
                              placeholder="0"
                              [control]="
                                getFormControl(entry, 'totalBuildingEnergy')
                              "
                              (change)="onChange()"
                            ></app-input>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].totalBuildingArea
                            }}</label>
                            <app-input
                              type="number"
                              placeholder="0"
                              unit="m²"
                              [control]="
                                getFormControl(entry, 'totalBuildingArea')
                              "
                              (change)="onChange()"
                            ></app-input>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].leasedArea
                            }}</label>
                            <app-input
                              type="number"
                              placeholder="0"
                              unit="m²"
                              [control]="getFormControl(entry, 'leasedArea')"
                              (change)="onChange()"
                            ></app-input>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].occupancyRate
                            }}</label>
                            <app-input
                              type="number"
                              placeholder="1"
                              min="0"
                              max="1"
                              step="0.1"
                              [control]="getFormControl(entry, 'occupancyRate')"
                              (change)="onChange()"
                            ></app-input>
                          </div>

                          <!-- <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].evidence
                            }}</label>
                            <app-input
                              type="file"
                              [control]="getFormControl(entry, 'evidence')"
                              (change)="onChange()"
                            ></app-input>
                          </div> -->
                        </div>
                      </ng-template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Other Leased Assets Section -->
          <div>
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium">
                {{ strings[language].otherLeasedAssets }}
              </h3>
              <button
                class="bg-[#F8F9FA] px-3 py-2 rounded text-white"
                (click)="addAssetSpecificLeasedOtherAssetRow()"
              >
                <app-plus></app-plus>
              </button>
            </div>

            <div class="space-y-4">
              <div
                *ngFor="
                  let control of assetSpecificLeasedOtherAssetControls;
                  let i = index
                "
                class="border rounded-lg p-4"
              >
                <div class="flex justify-end mb-4 cursor-pointer">
                  <button
                    (click)="removeRow('asset-specific-leased-other', i)"
                    class="text-red-500"
                  >
                    <app-trash></app-trash>
                  </button>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].assetLabel
                    }}</label>
                    <app-input
                      placeholder="{{
                        strings[language].assetLabelPlaceholder
                      }}"
                      [control]="control.row.assetLabel"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].lesseeEntity
                    }}</label>
                    <app-input
                      placeholder="{{
                        strings[language].lesseeEntityPlaceholder
                      }}"
                      [control]="control.row.lesseeEntity"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].assetCategory
                    }}</label>
                    <app-dropdown
                      placeholder="Asset Category"
                      [options]="vehicleCategories"
                      [control]="control.row.assetCategory"
                      (change)="onChange()"
                    ></app-dropdown>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].subtype
                    }}</label>
                    <app-dropdown
                      placeholder="Subtype/Description"
                      [options]="
                        getVehicleSubtypes(control.row.assetCategory.value)
                      "
                      [control]="control.row.subtype"
                      (change)="onChange()"
                    ></app-dropdown>
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].annualDistance
                    }}</label>
                    <app-input
                      placeholder="{{ strings[language].annualDistance }}"
                      [control]="control.row.annualDistance"
                      (change)="onChange()"
                    ></app-input>
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].reportingYear
                    }}</label>
                    <app-year-picker
                      [control]="control.row.reportingYear"
                      (yearChange)="onChange()"
                    ></app-year-picker>
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].country
                    }}</label>
                    <app-country-picker
                      [control]="control.row.country"
                      (change)="onChange()"
                    ></app-country-picker>
                  </div>
                  <!-- In the average leased buildings section -->
                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].leaseStartDate }}
                    </label>
                    <app-date-picker
                      [control]="control.row.leaseStartDate"
                      [maxDate]="control.row.leaseEndDate.value"
                      (dateChange)="
                        onDateChange($event, control.row.leaseStartDate)
                      "
                    ></app-date-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].leaseEndDate }}
                    </label>
                    <app-date-picker
                      [control]="control.row.leaseEndDate"
                      [minDate]="control.row.leaseStartDate.value"
                      [isEndDatePicker]="true"
                      (dateChange)="
                        onDateChange($event, control.row.leaseEndDate)
                      "
                    ></app-date-picker>
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].utilizationMetric
                    }}</label>
                    <app-dropdown
                      placeholder="{{
                        strings[language].selectUtilizationMetric
                      }}"
                      [options]="utilizationMetrics"
                      [control]="control.row.utilizationMetric"
                      (change)="onChange()"
                    ></app-dropdown>
                  </div>
                </div>

                <!-- Energy Entries -->
                <div class="mt-4">
                  <div class="flex justify-between items-center mb-4">
                    <h3 class="text-lg font-medium">
                      {{ strings[language].energyFuelEntries }}
                    </h3>
                    <button
                      class="bg-[#F8F9FA] px-3 py-2 rounded text-white"
                      (click)="
                        addAssetSpecificLeasedOtherAssetEnergyEntry(control)
                      "
                    >
                      <app-plus></app-plus>
                    </button>
                  </div>

                  <div class="space-y-4">
                    <div
                      *ngFor="
                        let entry of getLeasedOtherAssetEnergyEntries(control);
                        let j = index
                      "
                      class="border rounded p-4"
                    >
                      <div class="flex justify-end mb-2">
                        <button
                          (click)="
                            removeLeasedOtherAssetEnergyEntry(control, j)
                          "
                          class="text-red-500 text-sm"
                        >
                          <app-trash></app-trash>
                        </button>
                      </div>

                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label class="block text-sm font-medium mb-1">{{
                            strings[language].fuelType
                          }}</label>
                          <app-dropdown
                            placeholder="{{ strings[language].fuelType }}"
                            [options]="fuelTypes"
                            [control]="getFormControl(entry, 'fuelType')"
                            (change)="onFuelTypeChange(entry); onChange()"
                          ></app-dropdown>
                        </div>

                        <div>
                          <label class="block text-sm font-medium mb-1">{{
                            strings[language].unit
                          }}</label>
                          <app-dropdown
                            placeholder="{{ strings[language].unit }}"
                            [options]="fuelUnits"
                            [control]="getFormControl(entry, 'unit')"
                            (change)="onChange()"
                          ></app-dropdown>
                        </div>

                        <div>
                          <label class="block text-sm font-medium mb-1">{{
                            strings[language].amount
                          }}</label>
                          <app-input
                            type="number"
                            placeholder="0"
                            [control]="getFormControl(entry, 'amount')"
                            (change)="onChange()"
                          ></app-input>
                        </div>

                <div>
                   <label class="block text-sm font-medium text-gray-700 mb-1">
                      {{ strings[language].evidence }}
                    </label>
                         <div *ngIf="!getFormControl(entry, 'evidence').value" class="border border-[#CED4DA] flex hover:border-gray-400 items-center justify-between ng-star-inserted p-2 rounded-lg">
  <span class="text-sm text-gray-500 truncate">
    {{ strings[language].selectFile }}
  </span>
  <label>
    <app-file-upload-logo/>
    <input
      type="file"
      class="hidden"
      accept=".pdf"
      (change)="onFileChange($event, getFormControl(entry, 'evidence'))"
    />
  </label>
</div>

<div *ngIf="getFormControl(entry, 'evidence').value" class="bg-white border border-[#CED4DA] flex items-center justify-between ng-star-inserted p-3 rounded-lg">
  <span class="text-sm text-gray-700 truncate">
    {{ getFormControl(entry, 'evidence').value.name }}
  </span>
  <div class="flex gap-2">
    <button
      type="button"
      class="text-[#043B22] hover:text-[#032A1A]"
      (click)="getFormControl(entry, 'evidence').setValue(null); onChange()"
    >
      <app-trash></app-trash>
    </button>
    <label class="cursor-pointer text-[#043B22] hover:text-[#032A1A]">
      <input
        type="file"
        class="hidden"
        accept=".pdf"
        (change)="onFileChange($event, getFormControl(entry, 'evidence'))"
      />
    </label>
  </div>
</div>
                </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Lessee-Specific Method -->
        <div *ngIf="selectedMethod === 'lessee-specific'" class="space-y-6">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-medium">
              {{ strings[language].lesseeSpecificAssets }}
            </h3>
            <button
              class="bg-[#F8F9FA] px-3 py-2 rounded text-white"
              (click)="addLesseeSpecificRow()"
            >
              <app-plus></app-plus>
            </button>
          </div>

          <div class="space-y-4">
            <div
              *ngFor="let control of lesseeSpecificControls; let i = index"
              class="border rounded-lg p-4"
            >
              <div class="flex justify-end mb-4 cursor-pointer">
                <button
                  (click)="removeRow('lessee-specific', i)"
                  class="text-red-500"
                >
                  <app-trash></app-trash>
                </button>
              </div>

              <!-- Section A: Identification of the Asset -->
              <h4 class="text-md font-semibold mb-2">
                {{ strings[language].sectionA }}
              </h4>
              <div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].assetLabel }}
                    </label>
                    <app-input
                      placeholder="{{
                        strings[language].assetLabelPlaceholder
                      }}"
                      [control]="control.row.assetLabel"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].assetType }}
                    </label>
                    <app-dropdown
                      [options]="lesseeAssetTypes"
                      [control]="control.row.assetType"
                      (change)="onChange()"
                      placeholder="{{ strings[language].assetType }}"
                    ></app-dropdown>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].lesseeEntity }}
                    </label>
                    <app-input
                      placeholder="{{
                        strings[language].lesseeEntityPlaceholder
                      }}"
                      [control]="control.row.lesseeEntity"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].reportingYear }}
                    </label>
                    <app-year-picker
                      [control]="control.row.reportingYear"
                      (yearChange)="onChange()"
                    ></app-year-picker>
                  </div>
<div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].country
                    }}</label>
                    <app-country-picker
                      [control]="control.row.country"
                      (change)="onChange()"
                    ></app-country-picker>
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].leaseStartDate }}
                    </label>
                    <app-date-picker
                      [control]="control.row.leaseStartDate"
                      [maxDate]="control.row.leaseEndDate.value"
                      (dateChange)="
                        onDateChange($event, control.row.leaseStartDate)
                      "
                    ></app-date-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].leaseEndDate }}
                    </label>
                    <app-date-picker
                      [control]="control.row.leaseEndDate"
                      [minDate]="control.row.leaseStartDate.value"
                      [isEndDatePicker]="true"
                      (dateChange)="
                        onDateChange($event, control.row.leaseEndDate)
                      "
                    ></app-date-picker>
                  </div>
                </div>

                <!-- Section B: Lessee corporate emissions -->
                <h4 class="text-md font-semibold mt-6 mb-2">
                  {{ strings[language].sectionB }}
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].scope1Emissions }}
                    </label>
                    <app-input
                      type="number"
                      placeholder="0"
                      unit="kg CO₂e"
                      [control]="control.row.scope1Emissions"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].scope2Emissions }}
                    </label>
                    <app-input
                      type="number"
                      placeholder="0"
                      unit="kg CO₂e"
                      [control]="control.row.scope2Emissions"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].scope2Basis }}
                    </label>
                    <app-dropdown
                      [options]="scope2BasisOptions"
                      [control]="control.row.scope2Basis"
                      (change)="onChange()"
                      placeholder="{{ strings[language].scope2Basis }}"
                    ></app-dropdown>
                  </div>

                  <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      {{ strings[language].evidence }}
                    </label>

                    <div
                      *ngIf="!control.row.lesseeEvidence.value"
                      class="border border-[#CED4DA] flex hover:border-gray-400 items-center justify-between ng-star-inserted p-2 rounded-lg"
                    >
                      <span class="text-sm text-gray-500 truncate">
                        {{ strings[language].selectFile }}
                      </span>
                      <label
                      >
                       <app-file-upload-logo/>
                        <input
                          type="file"
                          class="hidden"
                          accept=".pdf"
                          (change)="onFileChange($event, control.row.lesseeEvidence)"
                        />
                      </label>
                    </div>

                    <div
                      *ngIf="control.row.lesseeEvidence.value"
                      class="bg-white border border-[#CED4DA] flex items-center justify-between ng-star-inserted p-3 rounded-lg"
                    >
                      <span class="text-sm text-gray-700 truncate">
                        {{ control.row.lesseeEvidence.value.name }}
                      </span>
                      <div class="flex gap-2">
                        <button
                          type="button"
                          class="text-[#043B22] hover:text-[#032A1A]"
                          (click)="control.row.lesseeEvidence.setValue(null);onChange()"
                        >
                          <app-trash></app-trash>
                        </button>
                        <label
                          class="cursor-pointer text-[#043B22] hover:text-[#032A1A]"
                        >
                          <input
                            type="file"
                            class="hidden"
                            accept=".pdf"
                            (change)="
                              onFileChange($event, control.row.lesseeEvidence)
                            "
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Section C: Allocation basis -->
                <h4 class="text-md font-semibold mt-6 mb-2">
                  {{ strings[language].sectionC }}
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].allocationMetric }}
                    </label>
                    <app-dropdown
                      [options]="allocationMetrics"
                      [control]="control.row.allocationMetric"
                      (change)="onChange()"
                      placeholder="{{ strings[language].allocationMetric }}"
                    ></app-dropdown>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].leasedQuantity }}
                    </label>
                    <app-input
                      type="number"
                      placeholder="0"
                      [control]="control.row.leasedQuantity"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].totalQuantity }}
                    </label>
                    <app-input
                      type="number"
                      placeholder="0"
                      [control]="control.row.totalQuantity"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div *ngIf="control.row.assetType.value === 'building'">
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].occupancyRate }}
                    </label>
                    <app-input
                      type="number"
                      placeholder="1"
                      [control]="control.row.occupancyRate"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      {{ strings[language].evidence }}
                    </label>

                    <div
                      *ngIf="!control.row.evidence.value"
                      class="border border-[#CED4DA] flex hover:border-gray-400 items-center justify-between ng-star-inserted p-2 rounded-lg"
                    >
                      <span class="text-sm text-gray-500 truncate">
                        {{ strings[language].selectFile }}
                      </span>
                      <label
                      >
                       <app-file-upload-logo/>
                        <input
                          type="file"
                          class="hidden"
                          accept=".pdf"
                          (change)="onFileChange($event, control.row.evidence)"
                        />
                      </label>
                    </div>

                    <div
                      *ngIf="control.row.evidence.value"
                      class="bg-white border border-[#CED4DA] flex items-center justify-between ng-star-inserted p-3 rounded-lg"
                    >
                      <span class="text-sm text-gray-700 truncate">
                        {{ control.row.evidence.value.name }}
                      </span>
                      <div class="flex gap-2">
                        <button
                          type="button"
                          class="text-[#043B22] hover:text-[#032A1A]"
                          (click)="control.row.evidence.setValue(null);onChange()"
                        >
                          <app-trash></app-trash>
                        </button>
                        <label
                          class="cursor-pointer text-[#043B22] hover:text-[#032A1A]"
                        >
                          <input
                            type="file"
                            class="hidden"
                            accept=".pdf"
                            (change)="
                              onFileChange($event, control.row.evidence)
                            "
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  standalone: true
})
export class Category5Question2Component {
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);
  categories = this.categoryService.categories();
  changeDetectorRef = inject(ChangeDetectorRef);
  usage = false;
  selectedMethod: 'average' | 'asset-specific' | 'lessee-specific' = 'average';

  // Form controls for each method
  averageBuildingControls: { row: AverageDataBuildingRow }[] = [];
  averageVehicleControls: { row: AverageDataVehicleRow }[] = [];
  assetSpecificBuildingControls: {
    row: AssetSpecificBuildingRow;
    energyEntries?: FormGroup[];
  }[] = [];
  assetSpecificVehicleControls: { row: AssetSpecificVehicleRow }[] = [];
  lessorSpecificControls: { row: LessorSpecificRow }[] = [];

  // New form controls for leased-out assets
  averageLeasedBuildingControls: { row: AverageDataLeasedBuildingRow }[] = [];
  averageLeasedOtherAssetControls: {
    row: AverageDataLeasedOtherAssetRow;
  }[] = [];
  assetSpecificLeasedBuildingControls: {
    row: AssetSpecificLeasedBuildingRow;
    entries?: AssetSpecificLeasedBuildingEntry[];
  }[] = [];
  assetSpecificLeasedOtherAssetControls: {
    row: AssetSpecificLeasedOtherAssetRow;
    energyEntries?: FormGroup<AssetSpecificLeasedOtherAssetEnergyEntry>[];
  }[] = [];
  lesseeSpecificControls: { row: LesseeSpecificRow }[] = [];

  // Dropdown options
  buildingTypes = [
    {
      value: 'office',
      label: this.language === 'english' ? 'Office' : 'Ufficio',
      activityId: 'real_estate-type_office',
      unit: 'm2'
    },
    {
      value: 'distribution-warehouse-warm',
      label:
        this.language === 'english'
          ? 'Distribution Warehouse - Warm'
          : 'Magazzino di distribuzione (non refrigerato)',
      activityId: 'real_estate-type_distribution_warehouse_warm',
      unit: 'm2'
    },
    {
      value: 'distribution-warehouse-cold',
      label:
        this.language === 'english'
          ? 'Distribution Warehouse - Cold'
          : 'Magazzino di distribuzione frigorifero',
      activityId: 'real_estate-type_distribution_warehouse_cold',
      unit: 'm2'
    },
    {
      value: 'retail-high-street',
      label:
        this.language === 'english'
          ? 'Retail - High Street'
          : 'Negozio al dettaglio - strada principale',
      activityId: 'real_estate-type_retail_high_street',
      unit: 'm2'
    },
    {
      value: 'retail-warehouse',
      label:
        this.language === 'english'
          ? 'Retail - Warehouse'
          : 'Negozio al dettaglio - magazzino',
      activityId: 'real_estate-type_retail_warehouse',
      unit: 'm2'
    },
    {
      value: 'retail-shopping-centre',
      label:
        this.language === 'english'
          ? 'Retail - Shopping Centre'
          : 'Negozio al dettaglio - centro commerciale',
      activityId: 'real_estate-type_retail_shopping_center',
      unit: 'm2'
    },
    {
      value: 'hotel',
      label: this.language === 'english' ? 'Hotel' : 'Hotel / Albergo',
      activityId: 'real_estate-type_hotel',
      unit: 'm2'
    },
    {
      value: 'healthcare',
      label:
        this.language === 'english'
          ? 'Healthcare'
          : 'Sanità / Assistenza sanitaria',
      activityId: 'real_estate-type_healthcare',
      unit: 'm2'
    },
    {
      value: 'leisure-lodging',
      label:
        this.language === 'english'
          ? 'Leisure/Lodging'
          : 'Tempo libero o alloggio',
      activityId: 'real_estate-type_leisure_lodging',
      unit: 'm2'
    },
    {
      value: 'non_residential',
      label:
        this.language === 'english'
          ? 'Non-residential (generic)'
          : 'Edificio non residenziale',
      activityId: 'real_estate-type_non_residential_total',
      unit: 'm2'
    }
  ];

  epcRatings = [
    {
      value: 'a',
      label: this.language === 'english' ? 'A (Outstanding)' : 'A (Eccellente)'
    },
    {
      value: 'b',
      label: this.language === 'english' ? 'B (Excellent)' : 'B (Ottimo)'
    },
    {
      value: 'c',
      label: this.language === 'english' ? 'C (Good)' : 'C (Buono)'
    },
    {
      value: 'd',
      label: this.language === 'english' ? 'D (Average)' : 'D (Medio)'
    },
    {
      value: 'e',
      label: this.language === 'english' ? 'E (Poor)' : 'E (Scarso)'
    },
    {
      value: 'f',
      label: this.language === 'english' ? 'F (Very Poor)' : 'F (Molto scarso)'
    },
    {
      value: 'not-provided',
      label: this.language === 'english' ? 'Not Provided' : 'Non fornito'
    }
  ];

  countries = [
    { value: 'US', label: 'United States' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'IT', label: 'Italy' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'ES', label: 'Spain' }
  ];

  vehicleCategories = [
    {
      value: 'Vehicles_and_trucks',
      label:
        this.language === 'english' ? 'Vehicles and trucks' : 'Veicoli e camion'
    }
  ];

  vehicleSubtypes: Record<string, any[]> = {
    Vehicles_and_trucks: [
      {
        value: 'passenger-car-small',
        label:
          this.language === 'english'
            ? 'Passenger car – small'
            : 'Auto passeggeri – piccola'
      },
      {
        value: 'passenger-car-medium',
        label:
          this.language === 'english'
            ? 'Passenger car – medium'
            : 'Auto passeggeri – media'
      },
      {
        value: 'light-van',
        label: this.language === 'english' ? 'Light van' : 'Furgone leggero'
      },
      {
        value: 'rigid-truck',
        label: this.language === 'english' ? 'Rigid truck' : 'Camion rigido'
      },
      {
        value: 'articulated-truck',
        label:
          this.language === 'english'
            ? 'Articulated truck'
            : 'Camion articolato'
      },
      {
        value: 'electric-car',
        label: this.language === 'english' ? 'Electric car' : 'Auto elettrica'
      },
      {
        value: 'plug-in-hybrid-car',
        label:
          this.language === 'english'
            ? 'Plug-in hybrid car'
            : 'Auto ibrida plug-in'
      },
      {
        value: 'motorcycle-scooter',
        label:
          this.language === 'english'
            ? 'Motorcycle / scooter'
            : 'Moto / scooter'
      }
    ]
  };

  lessorAssetTypes = [
    {
      value: 'building',
      label: this.language === 'english' ? 'Building' : 'Edificio'
    },
    {
      value: 'vehicle',
      label: this.language === 'english' ? 'Vehicle' : 'Veicolo'
    },
    {
      value: 'machinery',
      label: this.language === 'english' ? 'Machinery' : 'Macchinario'
    },
    {
      value: 'other',
      label: this.language === 'english' ? 'Other' : 'Altro'
    }
  ];

  // New dropdown options for leased-out assets
  leasedAssetCategories = [
    {
      value: 'vehicle',
      label: this.language === 'english' ? 'Vehicle' : 'Veicolo'
    },
    {
      value: 'mobile-machinery',
      label:
        this.language === 'english' ? 'Mobile machinery' : 'Macchinario mobile'
    },
    {
      value: 'office-equipment',
      label:
        this.language === 'english'
          ? 'Office equipment'
          : 'Attrezzatura per ufficio'
    },
    {
      value: 'it-equipment',
      label: this.language === 'english' ? 'IT equipment' : 'Attrezzatura IT'
    },
    {
      value: 'other',
      label: this.language === 'english' ? 'Other' : 'Altro'
    }
  ];

  utilizationMetrics = [
    {
      value: 'km',
      label: this.language === 'english' ? 'Kilometers' : 'Chilometri'
    },
    {
      value: 'engine-hours',
      label: this.language === 'english' ? 'Engine hours' : 'Ore motore'
    },
    {
      value: 'operating-hours',
      label: this.language === 'english' ? 'Operating hours' : 'Ore operative'
    }
  ];

  allocationMetrics = [
    {
      value: 'floor-area',
      label: this.language === 'english' ? 'Floor area' : 'Superficie'
    },
    {
      value: 'vehicle-km',
      label:
        this.language === 'english'
          ? 'Vehicle kilometers'
          : 'Chilometri veicolo'
    },
    {
      value: 'engine-hours',
      label: this.language === 'english' ? 'Engine hours' : 'Ore motore'
    },
    {
      value: 'units',
      label: this.language === 'english' ? 'Units' : 'Unità'
    },
    {
      value: 'headcount',
      label: this.language === 'english' ? 'Headcount' : 'Numero di persone'
    },
    {
      value: 'other',
      label: this.language === 'english' ? 'Other' : 'Altro'
    }
  ];

  scope2BasisOptions = [
    {
      value: 'market-based',
      label: this.language === 'english' ? 'Market-based' : 'Basato sul mercato'
    },
    {
      value: 'location-based',
      label:
        this.language === 'english'
          ? 'Location-based'
          : 'Basato sulla posizione'
    }
  ];

  lesseeAssetTypes = [
    {
      value: 'building',
      label: this.language === 'english' ? 'Building' : 'Edificio'
    },
    {
      value: 'vehicle',
      label: this.language === 'english' ? 'Vehicle' : 'Veicolo'
    },
    {
      value: 'machinery',
      label: this.language === 'english' ? 'Machinery' : 'Macchinario'
    },
    {
      value: 'it-equipment',
      label: this.language === 'english' ? 'IT equipment' : 'Attrezzatura IT'
    },
    {
      value: 'other',
      label: this.language === 'english' ? 'Other' : 'Altro'
    }
  ];

  fuelTypes = [
    {
      value: 'electricity-supply_grid-source_total_supplier_mix',
      label: this.language === 'english' ? 'Electricity' : 'Elettricità',
      unit: 'kWh' // Default unit for electricity
    },
    {
      value: 'fuel-type_natural_gas-fuel_use_na',
      label: this.language === 'english' ? 'Natural Gas' : 'Gas naturale',
      unit: 'm³' // Default unit for natural gas
    },
    {
      value: 'fuel-type_diesel-fuel_use_na',
      label: this.language === 'english' ? 'Diesel' : 'Gasolio',
      unit: 'liters' // Default unit for diesel
    },
    {
      value: 'fuel-type_heating_oil-fuel_use_stationary',
      label: this.language === 'english' ? 'Heating oil' : 'Heating oil',
      unit: 'liters' // Default unit for gasoline
    }
  ];

  fuelUnits = [
    { value: 'kWh', label: 'kWh' },
    { value: 'm³', label: 'm³' },
    {
      value: 'liters',
      label: this.language === 'english' ? 'Liters' : 'Litri'
    },
    { value: 'kg', label: 'kg' }
  ];

  strings: any = {
    english: {
      choice_yes: 'Yes',
      choice_no: 'No',
      answer: 'Please fill in the required data if yes to proceed.',
      selectMethod: 'Select Calculation Method',
      averageMethod: 'Average-Data Method',
      averageMethodDesc:
        'Use industry-average emission factors based on asset size/count',
      averageMethodDescLine1:
        'Choose this option if you cannot obtain meter readings or Scope 1 + 2 figures from the lessee.',
      averageMethodDescLine2: 'Emissions will be estimated using:',
      averageMethodDescLine3:
        '– The size, count, or usage of the asset (e.g. floor area in m², number of vehicles, operating hours), and',
      averageMethodDescLine4:
        '– An industry-average emission factor based on the asset type and country.',

      assetSpecificMethod: 'Asset-Specific Method',
      assetSpecificMethodDesc:
        'Use it when the lessee can supply actual energy- or fuel-consumption data for the specific asset you lease to them (utilities, sub-meter reads, fuel logs, refrigerant top-ups). This aligns with the GHG-Protocol “asset-specific method” for leased assets',
      lesseeSpecificMethod: 'Lessee-Specific Method',
      lesseeSpecificMethodDesc:
        'Choose this method only if the lessee can provide their corporate Scope 1 and Scope 2 emissions totals, but cannot supply energy data for the specific asset. You then apportion those emissions to your leased-out asset using a reasonable basis, such as floor area (m²), distance (km), engine hours, or headcount.',
      leasedBuildings: 'Leased-out Buildings & Real-Estate',
      otherLeasedAssets: 'Other Leased Assets',
      lesseeSpecificAssets: 'Leased Assets (Lessee-Specific)',
      assetLabel: 'Asset Label',
      assetLabelPlaceholder: 'e.g., Milan HQ - 3rd floor',
      buildingType: 'Building Type',
      epcRating: 'EPC Rating',
      country: 'Country/Region',
      reportingYear: 'Reporting Year',
      floorArea: 'Floor Area',
      assetCategory: 'Asset Category',
      subtype: 'Subtype/Description',
      quantity: 'Quantity',
      annualDistance: 'Annual Distance',
      utilizationMetric: 'Utilization Metric',
      selectUtilizationMetric: 'Select utilization metric',
      lesseeEntity: 'Lessee Entity',
      lesseeEntityPlaceholder: 'Legal name or code',
      leaseStartDate: 'Lease Start Date',
      leaseEndDate: 'Lease End Date',
      subMetered: 'Sub-metered Data?',
      subMeteredHelp:
        'You (or the lessee) have sub-metered data when the energy used by this leased space is measured separately from the rest of the building, or when utility invoices report actual consumption for this unit.',
      energyEntries: 'Energy/Emission Entries (Repeatable Inside Asset)',
      energyFuelEntries: 'Energy/Fuel Entries',
      category: 'Category',
      type: 'Type',
      unit: 'Unit of Measure',
      amount: 'Amount Consumed',
      totalBuildingEnergy: 'Total Building Energy Use',
      totalBuildingArea: 'Total Building Area',
      leasedArea: 'Leased-out Area',
      occupancyRate: 'Occupancy Rate',
      addEnergyEntry: 'Add Energy Entry',
      assetType: 'Asset Type',
      scope1Emissions: 'Scope 1 Emissions',
      scope2Emissions: 'Scope 2 Emissions',
      scope2Basis: 'Scope 2 Basis',
      leasedQuantity: 'Leased Quantity',
      totalQuantity: 'Total Quantity',
      select_year: 'Select year',
      subMeteredLabel: 'Sub-metered',
      notSubMetered: 'Not Sub-metered',
      sectionA: 'Section A: Identification of the Asset',
      sectionB: 'Section B: Lessee Corporate Emissions',
      sectionC: 'Section C: Allocation Basis',
      buildings: 'A. Buildings',
      Other_Leased_Assets: 'B. Other assets (Vehicles)',
      asset_Level_Block: 'Asset Level Block',
      fuelType: 'Fuel/Energy Type',
      evidence: 'Evidence',
      allocationMetric: 'Allocation Metric',
      endDateAfterStartDate: 'End date must be after start date',
      selectFile: 'No file selected',
      upload: 'Upload',
      change: 'Change',
      fileRequirements: 'Accepted formats: PDF. Max size: 5MB.',
      remove: 'Remove'
    },
    italian: {
      choice_yes: 'Sì',
      choice_no: 'No',
      answer:
        'Si prega di inserire i dati richiesti se la risposta è sì per procedere.',
      selectMethod: 'Seleziona Metodo di Calcolo',
      averageMethod: 'Metodo Dati Medi',
      averageMethodDesc:
        'Utilizza fattori di emissione medi del settore basati su dimensioni/conteggio degli asset',
      assetSpecificMethod: 'Metodo Specifico per Asset',
      assetSpecificMethodDesc:
        'Usa questa opzione quando il locatario può fornire dati reali sul consumo di energia o carburante per il bene specifico che gli hai concesso in locazione (bollette, letture da sotto-contatori, registri del carburante, ricariche di refrigerante). Questa modalità è conforme al “metodo specifico per bene” del GHG Protocol per i beni in locazione.',
      lesseeSpecificMethod: 'Metodo Specifico del Locatario',
      lesseeSpecificMethodDesc:
        'Scegli questo metodo solo se il locatario può fornire i totali delle sue emissioni Scope 1 e Scope 2 a livello aziendale, ma non può fornire dati energetici per il bene specifico. In tal caso, attribuisci una quota di tali emissioni al bene in locazione utilizzando un criterio ragionevole, come la superficie (m²), la distanza (km), le ore motore o il numero di dipendenti.',
      leasedBuildings: 'Edifici e immobili in locazione',
      otherLeasedAssets: 'Altri beni in locazione',
      lesseeSpecificAssets: 'Asset in Locazione (Specifico Locatario)',
      assetLabel: 'Etichetta Asset',
      assetLabelPlaceholder: 'es. Sede Milano - 3° piano',
      buildingType: 'Tipo di Edificio',
      epcRating: 'Classe Energetica',
      country: 'Paese/Regione',
      reportingYear: 'Anno di Riferimento',
      floorArea: 'Superficie',
      assetCategory: 'Categoria Asset',
      subtype: 'Sottotipo/Descrizione',
      quantity: 'Quantità',
      annualDistance: 'Distanza Annuale',
      utilizationMetric: 'Metrica di Utilizzo',
      selectUtilizationMetric: 'Seleziona metrica di utilizzo',
      lesseeEntity: 'Locatario',
      lesseeEntityPlaceholder: 'Nome legale o codice',
      leaseStartDate: 'Data Inizio Locazione',
      leaseEndDate: 'Data Fine Locazione',
      subMetered: 'Dati Sub-misurati?',
      subMeteredHelp:
        'Tu (o il locatario) disponi di dati sub-metered quando l’energia utilizzata da questo spazio in affitto è misurata separatamente rispetto al resto dell’edificio, oppure quando le fatture delle utenze riportano i consumi effettivi per questa unità.',
      energyEntries:
        "Voci di energia/emissioni (ripetibili all'interno della risorsa)",
      energyFuelEntries: 'Voci di Energia/Carburante',
      category: 'Categoria',
      type: 'Tipo',
      unit: 'Unità di Misura',
      amount: 'Quantità Consumata',
      totalBuildingEnergy: 'Consumo Energetico Totale Edificio',
      totalBuildingArea: 'Superficie Totale Edificio',
      leasedArea: 'Superficie Locata',
      occupancyRate: 'Tasso di Occupazione',
      addEnergyEntry: 'Aggiungi Voce Energia',
      assetType: 'Tipo di Asset',
      scope1Emissions: 'Emissioni Scope 1',
      scope2Emissions: 'Emissioni Scope 2',
      scope2Basis: 'Base Scope 2',
      leasedQuantity: 'Quantità Locata',
      totalQuantity: 'Quantità Totale',
      select_year: 'Seleziona anno',
      subMeteredLabel: 'Sotto-contatore',
      notSubMetered: 'Non sotto-contatore',
      sectionA: 'Sezione A: Identificazione del Bene',
      sectionB: 'Sezione B: Emissioni Corporate del Locatario',
      sectionC: 'Sezione C: Criterio di Allocazione',
      buildings: 'A. Edifici',
      Other_Leased_Assets: 'B. Altri beni (Veicoli)',
      asset_Level_Block: 'Blocco a livello di risorsa',
      fuelType: 'Tipo di Carburante/Energia',
      evidence: 'Prova',
      allocationMetric: 'Metrica di Allocazione',
      endDateAfterStartDate:
        'La data di fine deve essere successiva alla data di inizio',
      selectFile: 'Nessun file selezionato',
      upload: 'Carica',
      change: 'Cambia',
      fileRequirements: 'Formati accettati: PDF. Dimensione massima: 5MB.',
      remove: 'Rimuovi',
      averageMethodDescLine1:
        'Scegli questa opzione se non puoi ottenere le letture del contatore o i dati sulle emissioni Scope 1 + 2 dal locatario.',
      averageMethodDescLine2: 'Le emissioni saranno stimate utilizzando:',
      averageMethodDescLine3:
        '– La dimensione, il numero o l’utilizzo del bene (es. superficie in m², numero di veicoli, ore di funzionamento), e',
      averageMethodDescLine4:
        '– Un fattore di emissione medio del settore basato sul tipo di bene e sul paese.'
    }
  };

  PRODUCT_CATEGORIES: any[] = [
    {
      name: 'Energy and Fuel',
      itaName: 'Energia e carburanti',
      subcategories: [
        {
          id: 'electricity-supply_grid-source_total_supplier_mix',
          label: 'Electricity from grid',
          itaLabel: 'Elettricità dalla rete'
        },
        {
          id: 'fuel-type_natural_gas-fuel_use_na',
          label: 'Natural gas',
          itaLabel: 'Gas naturale'
        },
        {
          id: 'fuel-type_diesel-fuel_use_na',
          label: 'Diesel',
          itaLabel: 'Gasolio'
        },
        {
          id: 'fuel-type_heating_oil-fuel_use_stationary',
          label: 'Heating oil',
          itaLabel: 'Olio combustibile'
        }
      ]
    },
    {
      name: 'Refrigerant gas for air conditioning',
      itaName: 'Gas refrigeranti per aria condizionata',
      subcategories: [
        {
          id: 'fugitive_gas-type_hfc_23',
          label: 'HFC-23',
          itaLabel: 'HFC-23'
        },
        {
          id: 'fugitive_gas-type_hfc_236fa',
          label: 'HFC-236fa',
          itaLabel: 'HFC-236fa'
        },
        {
          id: 'fugitive_gas-type_hfc_143a',
          label: 'HFC-143a',
          itaLabel: 'HFC-143a'
        },
        {
          id: 'fugitive_gas-type_hfc_32',
          label: 'HFC-32',
          itaLabel: 'HFC-32'
        },
        {
          id: 'fugitive_gas-type_hfc_134a',
          label: 'HFC-134a',
          itaLabel: 'HFC-134a'
        },
        {
          id: 'fugitive_gas-type_hfc_227ea',
          label: 'HFC-227ea',
          itaLabel: 'HFC-227ea'
        },
        {
          id: 'fugitive_gas-type_hfc_125',
          label: 'HFC-125',
          itaLabel: 'HFC-125'
        },
        {
          id: 'fugitive_gas-type_r744',
          label: 'R-744 (CO₂)',
          itaLabel: 'R-744 (CO₂)'
        },
        {
          id: 'fugitive_gas-type_r290_propane',
          label: 'R-290 (Propane)',
          itaLabel: 'R-290 (Propano)'
        },
        {
          id: 'fugitive_gas-type_hcfc_22r22_chlorodifluoromethane',
          label: 'R-22',
          itaLabel: 'R-22'
        }
      ]
    },
    {
      name:
        'Gas for refrigeration systems (e.g., cold storage, commercial refrigeration)',
      itaName:
        'Gas per sistemi di refrigerazione (es. celle frigorifere, refrigerazione commerciale)',
      subcategories: [
        {
          id: 'fugitive_gas-type_hfc_23',
          label: 'HFC-23',
          itaLabel: 'HFC-23'
        },
        {
          id: 'fugitive_gas-type_hfc_236fa',
          label: 'HFC-236fa',
          itaLabel: 'HFC-236fa'
        },
        {
          id: 'fugitive_gas-type_hfc_143a',
          label: 'HFC-143a',
          itaLabel: 'HFC-143a'
        },
        {
          id: 'fugitive_gas-type_hfc_134a',
          label: 'HFC-134a',
          itaLabel: 'HFC-134a'
        },
        {
          id: 'fugitive_gas-type_hfc_125',
          label: 'HFC-125',
          itaLabel: 'HFC-125'
        },
        {
          id: 'fugitive_gas-type_hfc_152a',
          label: 'HFC-152a',
          itaLabel: 'HFC-152a'
        },
        {
          id: 'fugitive_gas-type_hfc_227ea',
          label: 'HFC-227ea',
          itaLabel: 'HFC-227ea'
        },
        {
          id: 'fugitive_gas-type_r507a',
          label: 'R-507A',
          itaLabel: 'R-507A'
        },
        {
          id: 'fugitive_gas-type_r744',
          label: 'R-744 (CO₂)',
          itaLabel: 'R-744 (CO₂)'
        },
        {
          id: 'fugitive_gas-type_r717',
          label: 'R-717 (Ammonia, NH₃)',
          itaLabel: 'R-717 (Ammoniaca, NH₃)'
        },
        {
          id: 'fugitive_gas-type_r404a',
          label: 'R-404A',
          itaLabel: 'R-404A'
        }
      ]
    }
  ];

  yearOptions = [
    { label: '2020', value: 2020 },
    { label: '2021', value: 2021 },
    { label: '2022', value: 2022 },
    { label: '2023', value: 2023 },
    { label: '2024', value: 2024 },
    { label: '2025', value: 2025 }
  ];
  base64pdf: any;
  ngOnInit() {
    this.loadSavedData();
    this.initializeDefaultRows();
  }

  initializeDefaultRows() {
    if (this.averageBuildingControls.length === 0) {
      this.addAverageBuildingRow();
    }
    if (this.averageVehicleControls.length === 0) {
      this.addAverageVehicleRow();
    }
    if (this.assetSpecificBuildingControls.length === 0) {
      this.addAssetSpecificBuildingRow();
    }
    if (this.assetSpecificVehicleControls.length === 0) {
      this.addAssetSpecificVehicleRow();
    }
    if (this.lessorSpecificControls.length === 0) {
      this.addLessorSpecificRow();
    }
    // Add these new initializations
    if (this.averageLeasedBuildingControls.length === 0) {
      this.addAverageLeasedBuildingRow();
    }
    if (this.averageLeasedOtherAssetControls.length === 0) {
      this.addAverageLeasedOtherAssetRow();
    }
    if (this.assetSpecificLeasedBuildingControls.length === 0) {
      this.addAssetSpecificLeasedBuildingRow();
    }
    if (this.assetSpecificLeasedOtherAssetControls.length === 0) {
      this.addAssetSpecificLeasedOtherAssetRow();
    }
    if (this.lesseeSpecificControls.length === 0) {
      this.addLesseeSpecificRow();
    }
  }

  loadSavedData() {
    const savedData = this.getSavedData();
    if (savedData) {
      this.usage = savedData.usage || false;
      this.selectedMethod = savedData.selectedMethod || 'average';

      // Load average building data
      if (savedData.averageBuildingControls) {
        this.averageBuildingControls = savedData.averageBuildingControls.map(
          (item: any) => ({
            row: {
              assetLabel: new FormControl(item.assetLabel),
              buildingType: new FormControl(
                item.buildingType,
                Validators.required
              ),
              epcRating: new FormControl(item.epcRating),
              country: new FormControl(item.country, Validators.required),
              reportingYear: new FormControl(
                item.reportingYear,
                Validators.required
              ),
              floorArea: new FormControl(item.floorArea, [
                Validators.required,
                Validators.min(0)
              ]),
              method: new FormControl('average') as FormControl<'average'>,
              assetType: new FormControl('building') as FormControl<'building'>
            }
          })
        );
      }

      // Load average vehicle data
      if (savedData.averageVehicleControls) {
        this.averageVehicleControls = savedData.averageVehicleControls.map(
          (item: any) => ({
            row: {
              assetLabel: new FormControl(item.assetLabel),
              assetCategory: new FormControl(
                item.assetCategory,
                Validators.required
              ),
              subtype: new FormControl(item.subtype, Validators.required),
              country: new FormControl(item.country),
              reportingYear: new FormControl(
                item.reportingYear,
                Validators.required
              ),
              quantity: new FormControl(item.quantity, [
                Validators.required,
                Validators.min(0)
              ]),
              annualDistance: new FormControl(item.annualDistance, [
                Validators.required,
                Validators.min(0)
              ]),
              method: new FormControl('average') as FormControl<'average'>,
              assetType: new FormControl('vehicle') as FormControl<'vehicle'>
            }
          })
        );
      }

      // Load asset-specific building data
      if (savedData.assetSpecificBuildingControls) {
        this.assetSpecificBuildingControls = savedData.assetSpecificBuildingControls.map(
          (item: any) => {
            const control = {
              row: {
                assetLabel: new FormControl(
                  item.assetLabel,
                  Validators.required
                ),
                reportingYear: new FormControl(
                  item.reportingYear,
                  Validators.required
                ),
                country: new FormControl(item.country, Validators.required),
                subMetered: new FormControl(
                  item.subMetered,
                  Validators.required
                ),
                method: new FormControl('asset-specific') as FormControl<
                  'asset-specific'
                >,
                assetType: new FormControl('building') as FormControl<
                  'building'
                >
              },
              energyEntries: [] as FormGroup[]
            };

            if (item.energyEntries) {
              control.energyEntries = item.energyEntries.map((entry: any) => {
                if (entry.isSubMetered) {
                  return new FormGroup({
                    category: new FormControl(
                      entry.category,
                      Validators.required
                    ),
                    type: new FormControl(entry.type, Validators.required),
                    unit: new FormControl(entry.unit, Validators.required),
                    amount: new FormControl(entry.amount, [
                      Validators.required,
                      Validators.min(0)
                    ]),
                    isSubMetered: new FormControl(true, { nonNullable: true })
                  }) as FormGroup<SubMeteredEntry>;
                } else {
                  return new FormGroup({
                    category: new FormControl(
                      entry.category,
                      Validators.required
                    ),
                    type: new FormControl(entry.type, Validators.required),
                    unit: new FormControl(entry.unit, Validators.required),
                    totalBuildingEnergy: new FormControl(
                      entry.totalBuildingEnergy,
                      [Validators.required, Validators.min(0)]
                    ),
                    totalBuildingArea: new FormControl(
                      entry.totalBuildingArea,
                      [Validators.required, Validators.min(0)]
                    ),
                    leasedArea: new FormControl(entry.leasedArea, [
                      Validators.required,
                      Validators.min(0)
                    ]),
                    occupancyRate: new FormControl(entry.occupancyRate || 1, [
                      Validators.required,
                      Validators.min(0),
                      Validators.max(1)
                    ]),
                    isSubMetered: new FormControl(false, { nonNullable: true })
                  }) as FormGroup<NonSubMeteredEntry>;
                }
              });
            }

            return control;
          }
        );
      }

      // Load asset-specific vehicle data
      if (savedData.assetSpecificVehicleControls) {
        this.assetSpecificVehicleControls = savedData.assetSpecificVehicleControls.map(
          (item: any) => ({
            row: {
              assetLabel: new FormControl(item.assetLabel),
              reportingYear: new FormControl(
                item.reportingYear,
                Validators.required
              ),
              country: new FormControl(item.country),
              assetCategory: new FormControl(
                item.assetCategory,
                Validators.required
              ),
              subtype: new FormControl(item.subtype, Validators.required),
              quantity: new FormControl(item.quantity, [
                Validators.required,
                Validators.min(0)
              ]),
              annualDistance: new FormControl(item.annualDistance, [
                Validators.required,
                Validators.min(0)
              ]),
              method: new FormControl('asset-specific') as FormControl<
                'asset-specific'
              >,
              assetType: new FormControl('vehicle') as FormControl<'vehicle'>
            }
          })
        );
      }

      // Load lessor-specific data
      if (savedData.lessorSpecificControls) {
        this.lessorSpecificControls = savedData.lessorSpecificControls.map(
          (item: any) => ({
            row: {
              lessorName: new FormControl(item.lessorName),
              assetType: new FormControl(item.assetType, Validators.required),
              reportingYear: new FormControl(
                item.reportingYear,
                Validators.required
              ),
              country: new FormControl(item.country, Validators.required),
              scope1Emissions: new FormControl(item.scope1Emissions, [
                Validators.required,
                Validators.min(0)
              ]),
              scope2Emissions: new FormControl(item.scope2Emissions, [
                Validators.required,
                Validators.min(0)
              ]),
              leasedQuantity: new FormControl(item.leasedQuantity, [
                Validators.required,
                Validators.min(0)
              ]),
              totalQuantity: new FormControl(item.totalQuantity, [
                Validators.required,
                Validators.min(0)
              ]),
              occupancyRate: new FormControl(item.occupancyRate || 1, [
                Validators.min(0),
                Validators.max(1)
              ]),
              method: new FormControl('lessor-specific') as FormControl<
                'lessor-specific'
              >
            }
          })
        );
      }

      // Load average leased building data
      if (savedData.averageLeasedBuildingControls) {
        this.averageLeasedBuildingControls = savedData.averageLeasedBuildingControls.map(
          (item: any) => ({
            row: {
              assetLabel: new FormControl(item.assetLabel),
              buildingType: new FormControl(
                item.buildingType,
                Validators.required
              ),
              epcRating: new FormControl(item.epcRating),
              country: new FormControl(item.country, Validators.required),
              reportingYear: new FormControl(
                item.reportingYear,
                Validators.required
              ),
              floorArea: new FormControl(item.floorArea, [
                Validators.required,
                Validators.min(0)
              ]),
              lesseeEntity: new FormControl(item.lesseeEntity),
              leaseStartDate: new FormControl(
                item.leaseStartDate ? new Date(item.leaseStartDate) : null
              ),
              leaseEndDate: new FormControl(
                item.leaseEndDate ? new Date(item.leaseEndDate) : null
              ),
              method: new FormControl('average') as FormControl<'average'>,
              assetType: new FormControl('building') as FormControl<'building'>
            }
          })
        );
      }

      // Load average leased other asset data
      if (savedData.averageLeasedOtherAssetControls) {
        this.averageLeasedOtherAssetControls = savedData.averageLeasedOtherAssetControls.map(
          (item: any) => ({
            row: {
              assetLabel: new FormControl(item.assetLabel),
              assetCategory: new FormControl(
                item.assetCategory,
                Validators.required
              ),
              subtype: new FormControl(item.subtype, Validators.required),
              country: new FormControl(item.country),
              reportingYear: new FormControl(
                item.reportingYear,
                Validators.required
              ),
              quantity: new FormControl(item.quantity, [
                Validators.required,
                Validators.min(0)
              ]),
              utilizationMetric: new FormControl(
                item.utilizationMetric,
                Validators.required
              ),
              lesseeEntity: new FormControl(item.lesseeEntity),
              leaseStartDate: new FormControl(
                item.leaseStartDate ? new Date(item.leaseStartDate) : null
              ),
              leaseEndDate: new FormControl(
                item.leaseEndDate ? new Date(item.leaseEndDate) : null
              ),
              method: new FormControl('average') as FormControl<'average'>,
              assetType: new FormControl('other') as FormControl<'other'>
            }
          })
        );
      }

      // Load asset-specific leased building data
      if (savedData.assetSpecificLeasedBuildingControls) {
        this.assetSpecificLeasedBuildingControls = savedData.assetSpecificLeasedBuildingControls.map(
          (item: any) => {
            const control = {
              row: {
                assetLabel: new FormControl(
                  item.assetLabel,
                  Validators.required
                ),
                lesseeEntity: new FormControl(item.lesseeEntity),
                reportingYear: new FormControl(
                  item.reportingYear,
                  Validators.required
                ),
                country: new FormControl(item.country, Validators.required),
                leaseStartDate: new FormControl(
                  item.leaseStartDate,
                  Validators.required
                ),
                leaseEndDate: new FormControl(
                  item.leaseEndDate,
                  Validators.required
                ),
                subMetered: new FormControl(
                  item.subMetered,
                  Validators.required
                ),
                occupancyRate: new FormControl(item.occupancyRate || 1, [
                  Validators.min(0),
                  Validators.max(1)
                ]),
                method: new FormControl('asset-specific') as FormControl<
                  'asset-specific'
                >,
                assetType: new FormControl('building') as FormControl<
                  'building'
                >
              },
              entries: [] as AssetSpecificLeasedBuildingEntry[]
            };

            if (item.entries) {
              control.entries = item.entries.map((entry: any) => {
                if (entry.isSubMetered) {
                  const evidenceValue = entry.evidence
                    ? {
                      name: entry.evidence.name || 'uploaded_file.pdf',
                      base64: entry.evidence.base64 || entry.evidence
                    }
                    : null;

                  return new FormGroup({
                    category: new FormControl(
                      entry.category,
                      Validators.required
                    ),
                    type: new FormControl(entry.type, Validators.required),
                    unit: new FormControl(entry.unit, Validators.required),
                    amount: new FormControl(entry.amount, [
                      Validators.required,
                      Validators.min(0)
                    ]),
                    evidence: new FormControl(evidenceValue),
                    isSubMetered: new FormControl(true, { nonNullable: true })
                  }) as FormGroup<AssetSpecificLeasedBuildingEnergyEntry>;
                } else {
                  return new FormGroup({
                    category: new FormControl(
                      entry.category,
                      Validators.required
                    ),
                    type: new FormControl(entry.type, Validators.required),
                    unit: new FormControl(entry.unit, Validators.required),
                    totalBuildingEnergy: new FormControl(
                      entry.totalBuildingEnergy,
                      [Validators.required, Validators.min(0)]
                    ),
                    totalBuildingArea: new FormControl(
                      entry.totalBuildingArea,
                      [Validators.required, Validators.min(0)]
                    ),
                    leasedArea: new FormControl(entry.leasedArea, [
                      Validators.required,
                      Validators.min(0)
                    ]),
                    occupancyRate: new FormControl(entry.occupancyRate || 1, [
                      Validators.required,
                      Validators.min(0),
                      Validators.max(1)
                    ]),
                    evidence: new FormControl(
                      item.evidence
                        ? {
                          name: item.evidence.name || 'uploaded_file.pdf',
                          base64: item.evidence.base64 || item.evidence
                        }
                        : null
                    ),
                    isSubMetered: new FormControl(false, { nonNullable: true })
                  }) as FormGroup<AssetSpecificLeasedBuildingAllocationEntry>;
                }
              });
            }

            return control;
          }
        );
      }

      // Load asset-specific leased other asset data
      if (savedData.assetSpecificLeasedOtherAssetControls) {
        this.assetSpecificLeasedOtherAssetControls = savedData.assetSpecificLeasedOtherAssetControls.map(
          (item: any) => ({
            row: {
              assetLabel: new FormControl(item.assetLabel),
              lesseeEntity: new FormControl(item.lesseeEntity),
              annualDistance: new FormControl(item.annualDistance),

              assetCategory: new FormControl(
                item.assetCategory,
                Validators.required
              ),
              subtype: new FormControl(item.subtype, Validators.required),
              reportingYear: new FormControl(
                item.reportingYear,
                Validators.required
              ),
              country: new FormControl(item.country),
              leaseStartDate: new FormControl(
                item.leaseStartDate,
                Validators.required
              ),
              leaseEndDate: new FormControl(
                item.leaseEndDate,
                Validators.required
              ),
              utilizationMetric: new FormControl(
                item.utilizationMetric,
                Validators.required
              ),
              method: new FormControl('asset-specific') as FormControl<
                'asset-specific'
              >,
              assetType: new FormControl('other') as FormControl<'other'>
            },
            energyEntries: item.energyEntries
              ? item.energyEntries.map((entry: any) => {
                const evidenceValue = entry.evidence
                  ? {
                    name: entry.evidence.name || 'uploaded_file.pdf',
                    base64: entry.evidence.base64 || entry.evidence
                  }
                  : null;

                return new FormGroup({
                  fuelType: new FormControl(
                    entry.fuelType,
                    Validators.required
                  ),
                  unit: new FormControl(entry.unit, Validators.required),
                  amount: new FormControl(entry.amount, [
                    Validators.required,
                    Validators.min(0)
                  ]),
                  evidence: new FormControl(evidenceValue)
                });
              })
              : []
          })
        );
      }

      // Load lessee-specific data
      if (savedData.lesseeSpecificControls) {
        this.lesseeSpecificControls = savedData.lesseeSpecificControls.map(
          (item: any) => ({
            row: {
              assetLabel: new FormControl(item.assetLabel, Validators.required),
              assetType: new FormControl(item.assetType, Validators.required),
              lesseeEntity: new FormControl(
                item.lesseeEntity,
                Validators.required
              ),
              evidence: new FormControl(
                item.evidence
                  ? {
                    name: item.evidence.name || 'uploaded_file.pdf',
                    base64: item.evidence.base64 || item.evidence
                  }
                  : null
              ),
              lesseeEvidence: new FormControl(
                item.lesseeEvidence
                  ? {
                    name: item.lesseeEvidence.name || 'uploaded_file.pdf',
                    base64: item.lesseeEvidence.base64 || item.lesseeEvidence
                  }
                  : null
              ),
              reportingYear: new FormControl(
                item.reportingYear,
                Validators.required
              ),
              country: new FormControl(item.country, Validators.required),
              leaseStartDate: new FormControl(
                item.leaseStartDate,
                Validators.required
              ),
              leaseEndDate: new FormControl(
                item.leaseEndDate,
                Validators.required
              ),
              scope1Emissions: new FormControl(item.scope1Emissions, [
                Validators.required,
                Validators.min(0)
              ]),
              scope2Emissions: new FormControl(item.scope2Emissions, [
                Validators.required,
                Validators.min(0)
              ]),
              scope2Basis: new FormControl(item.scope2Basis),
              allocationMetric: new FormControl(
                item.allocationMetric,
                Validators.required
              ),
              leasedQuantity: new FormControl(item.leasedQuantity, [
                Validators.required,
                Validators.min(0)
              ]),
              totalQuantity: new FormControl(item.totalQuantity, [
                Validators.required,
                Validators.min(0)
              ]),
              occupancyRate: new FormControl(item.occupancyRate || 1, [
                Validators.min(0),
                Validators.max(1)
              ]),
              method: new FormControl('lessee-specific') as FormControl<
                'lessee-specific'
              >
            }
          })
        );
      }
    }
  }

  getSavedData(): any {
    const categories = localStorage.getItem('categories');

    if (categories) {
      const parsed = JSON.parse(categories);
      return parsed?.[4]?.questions?.[1]?.answers || null;
    }
    return null;
  }

  selectMethod(method: 'average' | 'asset-specific' | 'lessee-specific') {
    this.selectedMethod = method;
    this.onChange();
  }

  // Average Data Method
  addAverageBuildingRow() {
    this.averageBuildingControls.push({
      row: {
        assetLabel: new FormControl(''),
        buildingType: new FormControl('', Validators.required),
        epcRating: new FormControl(''),
        country: new FormControl('', Validators.required),
        reportingYear: new FormControl('', Validators.required),
        floorArea: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        method: new FormControl('average') as FormControl<'average'>,
        assetType: new FormControl('building') as FormControl<'building'>
      }
    });
    this.onChange();
  }

  addAverageVehicleRow() {
    this.averageVehicleControls.push({
      row: {
        assetLabel: new FormControl(''),
        assetCategory: new FormControl('', Validators.required),
        subtype: new FormControl('', Validators.required),
        country: new FormControl(''),
        reportingYear: new FormControl('', Validators.required),
        quantity: new FormControl('', [Validators.required, Validators.min(0)]),
        annualDistance: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        method: new FormControl('average') as FormControl<'average'>,
        assetType: new FormControl('vehicle') as FormControl<'vehicle'>
      }
    });
    this.onChange();
  }

  // Asset-Specific Method
  addAssetSpecificBuildingRow() {
    const newControl = {
      row: {
        assetLabel: new FormControl<string | null>('', Validators.required),
        reportingYear: new FormControl<string | null>('', Validators.required),
        country: new FormControl<string | null>('', Validators.required),
        subMetered: new FormControl(true, {
          nonNullable: true,
          validators: Validators.required
        }),
        method: new FormControl<'asset-specific'>('asset-specific', {
          nonNullable: true
        }),
        assetType: new FormControl<'building'>('building', {
          nonNullable: true
        })
      },
      energyEntries: [] as FormGroup[]
    };

    this.assetSpecificBuildingControls.push(newControl);
    this.addEnergyEntry(newControl, true);
    this.onChange();
  }

  addAssetSpecificVehicleRow() {
    this.assetSpecificVehicleControls.push({
      row: {
        assetLabel: new FormControl(''),
        reportingYear: new FormControl('', Validators.required),
        country: new FormControl(''),
        assetCategory: new FormControl('', Validators.required),
        subtype: new FormControl('', Validators.required),
        quantity: new FormControl('', [Validators.required, Validators.min(0)]),
        annualDistance: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        method: new FormControl('asset-specific') as FormControl<
          'asset-specific'
        >,
        assetType: new FormControl('vehicle') as FormControl<'vehicle'>
      }
    });
    this.onChange();
  }

  addEnergyEntry(
    control: { row: AssetSpecificBuildingRow; energyEntries?: EnergyEntry[] },
    isSubMetered: boolean
  ) {
    if (!control.energyEntries) {
      control.energyEntries = [];
    }

    if (isSubMetered) {
      const newEntry = new FormGroup<SubMeteredEntry>({
        category: new FormControl<string | null>('', Validators.required),
        type: new FormControl<string | null>('', Validators.required),
        unit: new FormControl<string | null>('', Validators.required),
        amount: new FormControl<string | null>('', [
          Validators.required,
          Validators.min(0)
        ]),
        isSubMetered: new FormControl<true>(true, { nonNullable: true })
      });
      control.energyEntries.push(newEntry);
    } else {
      const newEntry = new FormGroup<NonSubMeteredEntry>({
        category: new FormControl<string | null>('', Validators.required),
        type: new FormControl<string | null>('', Validators.required),
        unit: new FormControl<string | null>('', Validators.required),
        totalBuildingEnergy: new FormControl<string | null>('', [
          Validators.required,
          Validators.min(0)
        ]),
        totalBuildingArea: new FormControl<string | null>('', [
          Validators.required,
          Validators.min(0)
        ]),
        leasedArea: new FormControl<string | null>('', [
          Validators.required,
          Validators.min(0)
        ]),
        occupancyRate: new FormControl<any>(1, [
          Validators.required,
          Validators.min(0),
          Validators.max(1)
        ]),
        isSubMetered: new FormControl<false>(false, { nonNullable: true })
      });
      control.energyEntries.push(newEntry);
    }

    this.onChange();
  }

  removeEnergyEntry(
    control: { row: AssetSpecificBuildingRow; energyEntries?: FormGroup[] },
    index: number
  ) {
    if (control.energyEntries && control.energyEntries.length > index) {
      control.energyEntries.splice(index, 1);
      this.onChange();
    }
  }

  getEnergyEntries(control: {
    row: AssetSpecificBuildingRow;
    energyEntries?: EnergyEntry[];
  }): EnergyEntry[] {
    return control.energyEntries || [];
  }

  // Lessor-Specific Method
  addLessorSpecificRow() {
    this.lessorSpecificControls.push({
      row: {
        lessorName: new FormControl(''),
        assetType: new FormControl('', Validators.required),
        reportingYear: new FormControl('', Validators.required),
        country: new FormControl('', Validators.required),
        scope1Emissions: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        scope2Emissions: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        leasedQuantity: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        totalQuantity: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        occupancyRate: new FormControl(1, [
          Validators.min(0),
          Validators.max(1)
        ]) as FormControl<number>,
        method: new FormControl('lessor-specific') as FormControl<
          'lessor-specific'
        >
      }
    });
    this.onChange();
  }

  // New methods for leased-out assets
  addAverageLeasedBuildingRow() {
    this.averageLeasedBuildingControls.push({
      row: {
        assetLabel: new FormControl(''),
        buildingType: new FormControl('', Validators.required),
        epcRating: new FormControl(''),
        country: new FormControl('', Validators.required),
        reportingYear: new FormControl('', Validators.required),
        floorArea: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        lesseeEntity: new FormControl(''),
        leaseStartDate: new FormControl<any | null>(null),
        leaseEndDate: new FormControl<any | null>(null),
        method: new FormControl('average') as FormControl<'average'>,
        assetType: new FormControl('building') as FormControl<'building'>
      }
    });
    this.onChange();
  }

  addAverageLeasedOtherAssetRow() {
    this.averageLeasedOtherAssetControls.push({
      row: {
        assetLabel: new FormControl(''),
        assetCategory: new FormControl('', Validators.required),
        subtype: new FormControl('', Validators.required),
        country: new FormControl(''),
        reportingYear: new FormControl('', Validators.required),
        quantity: new FormControl('', [Validators.required, Validators.min(0)]),
        utilizationMetric: new FormControl('', Validators.required),
        lesseeEntity: new FormControl(''),
        leaseStartDate: new FormControl(''),
        leaseEndDate: new FormControl(''),
        method: new FormControl('average') as FormControl<'average'>,
        assetType: new FormControl('other') as FormControl<'other'>
      }
    });
    this.onChange();
  }

  addAssetSpecificLeasedBuildingRow() {
    const newControl: any = {
      row: {
        assetLabel: new FormControl('', Validators.required),
        lesseeEntity: new FormControl(''),
        reportingYear: new FormControl('', Validators.required),
        country: new FormControl('', Validators.required),
        leaseStartDate: new FormControl('', Validators.required),
        leaseEndDate: new FormControl('', Validators.required),
        subMetered: new FormControl(true, Validators.required),
        occupancyRate: new FormControl(1, [
          Validators.min(0),
          Validators.max(1)
        ]),
        method: new FormControl('asset-specific') as FormControl<
          'asset-specific'
        >,
        assetType: new FormControl('building') as FormControl<'building'>
      },
      entries: [] as AssetSpecificLeasedBuildingEntry[]
    };

    this.assetSpecificLeasedBuildingControls.push(newControl);
    this.addAssetSpecificLeasedBuildingEntry(newControl, true);
    this.onChange();
  }

  addAssetSpecificLeasedBuildingEntry(
    control: {
      row: AssetSpecificLeasedBuildingRow;
      entries?: AssetSpecificLeasedBuildingEntry[];
    },
    isSubMetered: boolean
  ) {
    if (!control.entries) {
      control.entries = [];
    }

    if (isSubMetered) {
      const newEntry = new FormGroup<AssetSpecificLeasedBuildingEnergyEntry>({
        category: new FormControl('', Validators.required),
        type: new FormControl('', Validators.required),
        unit: new FormControl('', Validators.required),
        amount: new FormControl('', [Validators.required, Validators.min(0)]),
        evidence: new FormControl(null),
        isSubMetered: new FormControl(true, { nonNullable: true })
      });
      control.entries.push(newEntry);
    } else {
      const newEntry = new FormGroup<
        AssetSpecificLeasedBuildingAllocationEntry
      >({
        category: new FormControl('', Validators.required),
        type: new FormControl('', Validators.required),
        unit: new FormControl('', Validators.required),
        totalBuildingEnergy: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        totalBuildingArea: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        leasedArea: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        occupancyRate: new FormControl(control.row.occupancyRate.value || 1, [
          Validators.required,
          Validators.min(0),
          Validators.max(1)
        ]),
        evidence: new FormControl(''),
        isSubMetered: new FormControl(false, { nonNullable: true })
      });
      control.entries.push(newEntry);
    }
    this.onChange();
  }

  addAssetSpecificLeasedOtherAssetRow() {
    const newControl = {
      row: {
        assetLabel: new FormControl(''),
        lesseeEntity: new FormControl(''),
        assetCategory: new FormControl('', Validators.required),
        subtype: new FormControl('', Validators.required),
        annualDistance: new FormControl('', Validators.required),

        reportingYear: new FormControl('', Validators.required),
        country: new FormControl(''),
        leaseStartDate: new FormControl('', Validators.required),
        leaseEndDate: new FormControl('', Validators.required),
        utilizationMetric: new FormControl('', Validators.required),
        method: new FormControl('asset-specific') as FormControl<
          'asset-specific'
        >,
        assetType: new FormControl('other') as FormControl<'other'>
      },
      energyEntries: [] as FormGroup<AssetSpecificLeasedOtherAssetEnergyEntry>[]
    };

    this.assetSpecificLeasedOtherAssetControls.push(newControl);
    this.addAssetSpecificLeasedOtherAssetEnergyEntry(newControl);
    this.onChange();
  }

  addAssetSpecificLeasedOtherAssetEnergyEntry(control: {
    row: AssetSpecificLeasedOtherAssetRow;
    energyEntries?: FormGroup<AssetSpecificLeasedOtherAssetEnergyEntry>[];
  }) {
    if (!control.energyEntries) {
      control.energyEntries = [];
    }

    const newEntry = new FormGroup<AssetSpecificLeasedOtherAssetEnergyEntry>({
      fuelType: new FormControl('', Validators.required),
      unit: new FormControl('', Validators.required),
      amount: new FormControl('', [Validators.required, Validators.min(0)]),
      evidence: new FormControl(null)
    });

    control.energyEntries.push(newEntry);
    this.onChange();
  }

  addLesseeSpecificRow() {
    this.lesseeSpecificControls.push({
      row: {
        assetLabel: new FormControl('', Validators.required),
        assetType: new FormControl('', Validators.required),
        lesseeEntity: new FormControl('', Validators.required),
        evidence: new FormControl(''),
        lesseeEvidence: new FormControl(''),

        reportingYear: new FormControl('', Validators.required),
        country: new FormControl('', Validators.required),
        leaseStartDate: new FormControl('', Validators.required),
        leaseEndDate: new FormControl('', Validators.required),
        scope1Emissions: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        scope2Emissions: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        scope2Basis: new FormControl(''),
        allocationMetric: new FormControl('', Validators.required),
        leasedQuantity: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        totalQuantity: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        occupancyRate: new FormControl(1, [
          Validators.min(0),
          Validators.max(1)
        ]),
        method: new FormControl('lessee-specific') as FormControl<
          'lessee-specific'
        >
      }
    });
    this.onChange();
  }

  // Helper methods
  getVehicleSubtypes(category: string | null): any[] {
    if (!category) return [];
    return this.vehicleSubtypes[category] || [];
  }

  // Define unit mappings for specific types
  getEnergyUnits(typeId: string | null): any[] {
    if (!typeId) {
      return [
        {
          value: null,
          label:
            this.language === 'italian'
              ? 'Seleziona un tipo prima'
              : 'Select a type first',
          disabled: true
        }
      ];
    }

    // Define unit mappings for specific types
    const unitMappings: Record<string, any[]> = {
      // Electricity types
      'electricity-supply_grid-source_total_supplier_mix': [
        { value: 'kWh', label: 'kWh' },
        { value: 'MWh', label: 'MWh' }
      ],
      // Fuel types
      'fuel-type_natural_gas-fuel_use_na': [{ value: 'm³', label: 'm³' }],
      'fuel-type_diesel-fuel_use_na': [{ value: 'liters', label: 'liters' }],
      'fuel-type_heating_oil-fuel_use_stationary': [
        { value: 'liters', label: 'liters' }
      ],
      // Refrigerant gases - ALL USE m³ INSTEAD OF kg
      'fugitive_gas-type_hfc_23': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_hfc_236fa': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_hfc_143a': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_hfc_32': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_hfc_134a': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_hfc_227ea': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_hfc_125': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_r744': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_r290_propane': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_hcfc_22r22_chlorodifluoromethane': [
        { value: 'm³', label: 'm³' }
      ],
      'fugitive_gas-type_hfc_152a': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_r507a': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_r717': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_r404a': [{ value: 'm³', label: 'm³' }]
    };

    return unitMappings[typeId];
  }

  getFormControl(entry: AbstractControl, controlName: string): FormControl {
    return (entry as FormGroup).get(controlName) as FormControl;
  }

  getValue(entry: AbstractControl, controlName: string): any {
    return (entry as FormGroup).get(controlName)?.value;
  }

  onToggleChange(
    control:
      | { row: AssetSpecificBuildingRow; energyEntries?: FormGroup[] }
      | {
        row: AssetSpecificLeasedBuildingRow;
        entries?: AssetSpecificLeasedBuildingEntry[];
      }
  ) {
    if (this.isLeasedBuildingControl(control)) {
      // For leased building controls
      control.entries = [];
      this.addAssetSpecificLeasedBuildingEntry(
        control,
        control.row.subMetered.value
      );
    } else {
      // For regular building controls
      control.energyEntries = [];
      this.addEnergyEntry(control, control.row.subMetered.value);
    }

    // Force change detection
    this.changeDetectorRef.detectChanges();
  }

  // Type guard to check if the control is for leased buildings
  private isLeasedBuildingControl(
    control:
      | { row: AssetSpecificBuildingRow; energyEntries?: FormGroup[] }
      | {
        row: AssetSpecificLeasedBuildingRow;
        entries?: AssetSpecificLeasedBuildingEntry[];
      }
  ): control is {
    row: AssetSpecificLeasedBuildingRow;
    entries?: AssetSpecificLeasedBuildingEntry[];
  } {
    return (control as any).row.leaseStartDate !== undefined;
  }

  // Remove rows
  removeRow(
    type:
      | 'average-building'
      | 'average-vehicle'
      | 'asset-specific-building'
      | 'asset-specific-vehicle'
      | 'lessor-specific'
      | 'average-leased-building'
      | 'average-leased-other'
      | 'asset-specific-leased-building'
      | 'asset-specific-leased-other'
      | 'lessee-specific',
    index: number
  ) {
    switch (type) {
      case 'average-building':
        if (this.averageBuildingControls.length > index) {
          this.averageBuildingControls.splice(index, 1);
        }
        break;
      case 'average-vehicle':
        if (this.averageVehicleControls.length > index) {
          this.averageVehicleControls.splice(index, 1);
        }
        break;
      case 'asset-specific-building':
        if (this.assetSpecificBuildingControls.length > index) {
          this.assetSpecificBuildingControls.splice(index, 1);
        }
        break;
      case 'asset-specific-vehicle':
        if (this.assetSpecificVehicleControls.length > index) {
          this.assetSpecificVehicleControls.splice(index, 1);
        }
        break;
      case 'lessor-specific':
        if (this.lessorSpecificControls.length > index) {
          this.lessorSpecificControls.splice(index, 1);
        }
        break;
      case 'average-leased-building':
        if (this.averageLeasedBuildingControls.length > index) {
          this.averageLeasedBuildingControls.splice(index, 1);
        }
        break;
      case 'average-leased-other':
        if (this.averageLeasedOtherAssetControls.length > index) {
          this.averageLeasedOtherAssetControls.splice(index, 1);
        }
        break;
      case 'asset-specific-leased-building':
        if (this.assetSpecificLeasedBuildingControls.length > index) {
          this.assetSpecificLeasedBuildingControls.splice(index, 1);
        }
        break;
      case 'asset-specific-leased-other':
        if (this.assetSpecificLeasedOtherAssetControls.length > index) {
          this.assetSpecificLeasedOtherAssetControls.splice(index, 1);
        }
        break;
      case 'lessee-specific':
        if (this.lesseeSpecificControls.length > index) {
          this.lesseeSpecificControls.splice(index, 1);
        }
        break;
    }
    this.onChange();
  }

  removeLeasedBuildingEntry(
    control: {
      row: AssetSpecificLeasedBuildingRow;
      entries?: AssetSpecificLeasedBuildingEntry[];
    },
    index: number
  ) {
    if (control.entries && control.entries.length > index) {
      control.entries.splice(index, 1);
      this.onChange();
    }
  }

  removeLeasedOtherAssetEnergyEntry(
    control: {
      row: AssetSpecificLeasedOtherAssetRow;
      energyEntries?: FormGroup<AssetSpecificLeasedOtherAssetEnergyEntry>[];
    },
    index: number
  ) {
    if (control.energyEntries && control.energyEntries.length > index) {
      control.energyEntries.splice(index, 1);
      this.onChange();
    }
  }

  getLeasedBuildingEntries(control: {
    row: AssetSpecificLeasedBuildingRow;
    entries?: AssetSpecificLeasedBuildingEntry[];
  }): AssetSpecificLeasedBuildingEntry[] {
    return control.entries || [];
  }

  getLeasedOtherAssetEnergyEntries(control: {
    row: AssetSpecificLeasedOtherAssetRow;
    energyEntries?: FormGroup<AssetSpecificLeasedOtherAssetEnergyEntry>[];
  }): FormGroup<AssetSpecificLeasedOtherAssetEnergyEntry>[] {
    return control.energyEntries || [];
  }

  onChange = () => {
    if (this.categories && this.categories.length > 3) {
      let dataToSave: any = {
        usage: this.usage,
        selectedMethod: this.selectedMethod
      };

      // Save only the selected method's data
      if (this.selectedMethod === 'average') {
        dataToSave.averageBuildingControls = this.averageBuildingControls.map(
          control => ({
            assetLabel: control.row.assetLabel.value,
            buildingType: control.row.buildingType.value,
            epcRating: control.row.epcRating.value,
            country: control.row.country.value,
            reportingYear: control.row.reportingYear.value,
            floorArea: control.row.floorArea.value,
            method: control.row.method.value,
            assetType: control.row.assetType.value
          })
        );

        dataToSave.averageVehicleControls = this.averageVehicleControls.map(
          control => ({
            assetLabel: control.row.assetLabel.value,
            assetCategory: control.row.assetCategory.value,
            subtype: control.row.subtype.value,
            country: control.row.country.value,
            reportingYear: control.row.reportingYear.value,
            quantity: control.row.quantity.value,
            annualDistance: control.row.annualDistance.value,
            method: control.row.method.value,
            assetType: control.row.assetType.value
          })
        );

        dataToSave.averageLeasedBuildingControls = this.averageLeasedBuildingControls.map(
          control => ({
            assetLabel: control.row.assetLabel.value,
            buildingType: control.row.buildingType.value,
            epcRating: control.row.epcRating.value,
            country: control.row.country.value,
            reportingYear: control.row.reportingYear.value,
            floorArea: control.row.floorArea.value,
            lesseeEntity: control.row.lesseeEntity.value,
            leaseStartDate: control.row.leaseStartDate.value,
            leaseEndDate: control.row.leaseEndDate.value,
            method: control.row.method.value,
            assetType: control.row.assetType.value
          })
        );

        dataToSave.averageLeasedOtherAssetControls = this.averageLeasedOtherAssetControls.map(
          control => ({
            assetLabel: control.row.assetLabel.value,
            assetCategory: control.row.assetCategory.value,
            subtype: control.row.subtype.value,
            country: control.row.country.value,
            reportingYear: control.row.reportingYear.value,
            quantity: control.row.quantity.value,
            utilizationMetric: control.row.utilizationMetric.value,
            lesseeEntity: control.row.lesseeEntity.value,
            leaseStartDate: control.row.leaseStartDate.value,
            leaseEndDate: control.row.leaseEndDate.value,
            method: control.row.method.value,
            assetType: control.row.assetType.value
          })
        );
      } else if (this.selectedMethod === 'asset-specific') {
        dataToSave.assetSpecificBuildingControls = this.assetSpecificBuildingControls.map(
          control => ({
            assetLabel: control.row.assetLabel.value,
            reportingYear: control.row.reportingYear.value,
            country: control.row.country.value,
            subMetered: control.row.subMetered.value,
            method: control.row.method.value,
            assetType: control.row.assetType.value,
            energyEntries:
              control.energyEntries?.map(entry => entry.value) || []
          })
        );

        dataToSave.assetSpecificVehicleControls = this.assetSpecificVehicleControls.map(
          control => ({
            assetLabel: control.row.assetLabel.value,
            reportingYear: control.row.reportingYear.value,
            country: control.row.country.value,
            assetCategory: control.row.assetCategory.value,
            subtype: control.row.subtype.value,
            quantity: control.row.quantity.value,
            annualDistance: control.row.annualDistance.value,
            method: control.row.method.value,
            assetType: control.row.assetType.value
          })
        );

        dataToSave.assetSpecificLeasedBuildingControls = this.assetSpecificLeasedBuildingControls.map(
          control => ({
            assetLabel: control.row.assetLabel.value,
            lesseeEntity: control.row.lesseeEntity.value,
            reportingYear: control.row.reportingYear.value,
            country: control.row.country.value,
            leaseStartDate: control.row.leaseStartDate.value,
            leaseEndDate: control.row.leaseEndDate.value,
            subMetered: control.row.subMetered.value,
            occupancyRate: control.row.occupancyRate.value,
            method: control.row.method.value,
            assetType: control.row.assetType.value,
            entries:
              control.entries?.map(entry => ({
                ...entry.value,
                evidence: entry.value.evidence // Ensure evidence is included
              })) || []
          })
        );

        dataToSave.assetSpecificLeasedOtherAssetControls = this.assetSpecificLeasedOtherAssetControls.map(
          control => ({
            assetLabel: control.row.assetLabel.value,
            lesseeEntity: control.row.lesseeEntity.value,
            annualDistance: control.row.annualDistance.value,
            assetCategory: control.row.assetCategory.value,
            subtype: control.row.subtype.value,
            reportingYear: control.row.reportingYear.value,
            country: control.row.country.value,
            leaseStartDate: control.row.leaseStartDate.value,
            leaseEndDate: control.row.leaseEndDate.value,
            utilizationMetric: control.row.utilizationMetric.value,
            method: control.row.method.value,
            assetType: control.row.assetType.value,
            energyEntries:
              control.energyEntries?.map(entry => ({
                fuelType: entry.value.fuelType,
                unit: entry.value.unit,
                amount: entry.value.amount,
                evidence: entry.value.evidence
              })) || []
          })
        );
      } else if (this.selectedMethod === 'lessee-specific') {
        dataToSave.lesseeSpecificControls = this.lesseeSpecificControls.map(
          control => ({
            assetLabel: control.row.assetLabel.value,
            assetType: control.row.assetType.value,
            lesseeEntity: control.row.lesseeEntity.value,
            reportingYear: control.row.reportingYear.value,
            country: control.row.country.value,
            evidence: control.row.evidence.value,
            lesseeEvidence: control.row.lesseeEvidence.value,
            leaseStartDate: control.row.leaseStartDate.value,
            leaseEndDate: control.row.leaseEndDate.value,
            scope1Emissions: control.row.scope1Emissions.value,
            scope2Emissions: control.row.scope2Emissions.value,
            scope2Basis: control.row.scope2Basis.value,
            allocationMetric: control.row.allocationMetric.value,
            leasedQuantity: control.row.leasedQuantity.value,
            totalQuantity: control.row.totalQuantity.value,
            occupancyRate: control.row.occupancyRate.value,
            method: control.row.method.value
          })
        );
      }

      // Store only selected method data
      if (!this.categories[4].questions[1]) {
        this.categories[4].questions[1] = { answers: {} } as any;
      }
      this.categories[4].questions[1].answers = dataToSave;

      this.categoryService.persistCategories(this.categories);
    }
  };

  private _energyCategoryCache: { lang: string; opts: { value: string; label: string }[] } | null = null;

  get energyCategoryOptions(): { value: string; label: string }[] {
    const lang = this.language;
    if (this._energyCategoryCache?.lang === lang) {
      return this._energyCategoryCache.opts;
    }
    const opts = this.PRODUCT_CATEGORIES.map(category => ({
      value: category.name,
      label: lang === 'italian' ? category.itaName : category.name
    }));
    this._energyCategoryCache = { lang, opts };
    return opts;
  }

  onTypeChange(entry: FormGroup) {
    const typeValue = entry.get('type')?.value;
    const unitControl = entry.get('unit');

    if (typeValue && unitControl) {
      // Get the first available unit for this type
      const units = this.getEnergyUnits(typeValue);
      if (units.length > 0) {
        unitControl.setValue(units[0].value);
      }
    }

    this.onChange();
  }

  getEnergyTypes(categoryName: string | null): any[] {
    if (!categoryName) {
      return [
        {
          value: null,
          label:
            this.language === 'italian'
              ? 'Seleziona prima una categoria'
              : 'Select a category first',
          disabled: true
        }
      ];
    }

    const category = this.PRODUCT_CATEGORIES.find(c => c.name === categoryName);
    if (!category || !category.subcategories) return [];

    return category.subcategories.map((subcategory: any) => ({
      value: subcategory.id,
      label:
        this.language === 'italian' ? subcategory.itaLabel : subcategory.label
    }));
  }

  onCategoryChange(entry: FormGroup) {
    // Reset the type and unit controls when category changes
    entry.get('type')?.reset();
    entry.get('unit')?.reset();
    this.onChange();
  }

  getTypePlaceholder(entry: any): string {
    if (!this.getFormControl(entry, 'category').value) {
      return this.language === 'english'
        ? 'Select a category first'
        : 'Seleziona prima una categoria';
    } else {
      return this.language === 'english' ? 'Select type' : 'Seleziona il tipo';
    }
  }

  getUnitPlaceholder(entry: any): string {
    if (!this.getFormControl(entry, 'type').value) {
      return this.language === 'english'
        ? 'Select a type first'
        : 'Seleziona prima un tipo';
    } else {
      return this.language === 'english'
        ? 'Unit will auto-select'
        : "L'unità verrà selezionata automaticamente";
    }
  }

  toggleUsage(value: boolean): void {
    this.usage = value;

    if (!value) {
      // Clear all form data when switching to "No"
      this.averageBuildingControls = [];
      this.averageVehicleControls = [];
      this.assetSpecificBuildingControls = [];
      this.assetSpecificVehicleControls = [];
      this.lessorSpecificControls = [];
      this.averageLeasedBuildingControls = [];
      this.averageLeasedOtherAssetControls = [];
      this.assetSpecificLeasedBuildingControls = [];
      this.assetSpecificLeasedOtherAssetControls = [];
      this.lesseeSpecificControls = [];
    } else {
      // Initialize default rows when switching to "Yes"
      this.initializeDefaultRows();
    }

    this.onChange();
  }
  onDateChange(date: any, control: FormControl) {
    control.setValue(date);
    this.onChange(); // Manually trigger the save
  }
  startDate: Date | null = null;
  endDate: Date | null = null;

  onStartDateChange(
    date: Date | null,
    endDateControl: FormControl,
    leaseStartDate: FormControl
  ) {
    this.startDate = date;
    // Reset end date if it's before the new start date
    if (this.endDate && date && this.endDate < date) {
      this.endDate = null;
      // Also clear the form control if needed
      endDateControl.setValue(null);
    }
    this.onDateChange(date, leaseStartDate);
  }

  onEndDateChange(
    date: Date | null,
    startDateControl: FormControl,
    endDateControl: FormControl
  ) {
    this.endDate = date;
    // Reset start date if it's after the new end date
    if (this.startDate && date && this.startDate > date) {
      this.startDate = null;
      // Also clear the form control if needed
      startDateControl?.setValue(null);
    }
    this.onDateChange(date, endDateControl);
  }
  async onFileChange(event: Event, control: FormControl): Promise<void> {
    const input = event?.target as HTMLInputElement;
    if (!input?.files?.length) return;

    const file = input.files[0];

    // File type check
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      console.error('Please upload a valid PDF file.');
      control.setErrors({ invalidFileType: true });
      return;
    }

    try {
      const base64 = await this.pdfToBase64(file);
      const fileData: any = {
        name: file.name,
        base64: base64
      };

      control.setValue(fileData);
      control.updateValueAndValidity();
      this.onChange();
      this.onChange();
    } catch (error) {
      console.error('Conversion failed:', error);
      control.setErrors({ conversionError: true });
    }
  }

  pdfToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Extract just the base64 part (remove data:application/pdf;base64, prefix)
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  }
  onFuelTypeChange(entry: FormGroup<AssetSpecificLeasedOtherAssetEnergyEntry>) {
    const fuelTypeValue = entry.controls.fuelType.value;
    const unitControl = entry.controls.unit;

    if (fuelTypeValue && unitControl) {
      // Find the selected fuel type
      const selectedFuel = this.fuelTypes.find(f => f.value === fuelTypeValue);
      if (selectedFuel) {
        // Set the corresponding unit
        unitControl.setValue(selectedFuel.unit);
      }
    }
    this.onChange();
  }
}
